/* =========================================================================
 * converter.js — v5.1 (explicit convert button + two-strip layout + RU/EN i18n)
 * ========================================================================= */
(() => {
  'use strict';

  // ---- i18n ----
  // FORM_I18N — form-level / capability-driven runtime strings only.
  // Static-HTML head-section strings (title, og:*, nav_brand, etc.)
  // and the brand fallback are emitted at image-build time by sitegen
  // (CR-073 / CR-079 / CR-110); the share-button text derives the
  // brand from the page title's "— <brand>" suffix (CR-110). Russian
  // ships full parity; missing RU keys fall back to EN. Keys listed
  // in DE-009 §1.
  const FORM_I18N = {
    en: {
      // nav / brand
      nav_privacy: 'privacy',
      nav_faq: 'faq',
      nav_repo: 'repo',
      nav_how: 'how it works',
      lang_en: 'EN',
      lang_ru: 'RU',
      // share (CR-046; CR-063: share_btn text removed; button is icon-only)
      share_copied: 'link copied',
      share_failed: 'could not copy link',
      // hero
      hero_title: '.img converter',
      hero_sub: 'drop one or many images, click Convert, compare, download. <span class="accent">nothing is uploaded to third parties.</span>',
      // CR-082: the head-section strings (see i18n/en.yaml) are
      // baked into the served HTML at image-build time by sitegen
      // (CR-073 / CR-079). Keeping them in this catalog would
      // silently desync from the per-locale values the static HTML
      // carries; scripts/check_no_head_keys_in_js.sh fails when
      // either string reappears here.
      // dropzone
      drop_title: 'drop an image here',
      drop_sub: 'or click to browse · paste from clipboard works too',
      drop_formats: 'png · jpg · webp · gif · bmp · avif · heic · heif · jxl',
      // format / quality
      format_label: 'to',
      quality_label: 'quality — lower means smaller file, higher means better quality',
      trade_size: 'size',
      trade_quality: 'quality',
      quality_desc_tiny: 'tiny file · visible artifacts',
      quality_desc_small: 'small file · ok quality',
      quality_desc_balanced: 'balanced — good for web',
      quality_desc_high: 'high quality · modest size',
      quality_desc_near: 'near lossless',
      quality_desc_lossless: 'visually lossless',
      tier_l: 'small',
      tier_b: 'balanced',
      tier_h: 'high',
      tier_x: 'lossless',
      // resize
      resize_label: 'resize',
      resize_cap: 'cap dimension',
      resize_axis_longest: 'longest',
      resize_axis_width: 'width',
      resize_axis_height: 'height',
      resize_axis_auto: 'auto (L: w · P: h)',
      resize_x: '×',
      resize_px: 'px',
      // convert button + progress
      convert_btn: 'Convert',
      convert_busy: 'Converting',
      convert_idle: 'Converting 0 of 0…',
      convert_progress: 'Converting {k} of {n}…',
      add_more: '+ add more',
      add_more_title: 'add more files',
      download_all: 'download all (.zip)',
      // staging
      staging_title: '// staging',
      staging_progress: '{pending} pending',
      // results
      results_title: '// results',
      results_none: 'no conversions yet — click Convert to begin',
      results_clear: 'clear results',
      // compare expander
      expander_download: 'Download',
      expander_close: 'close',
      expander_original: 'original',
      expander_after: 'after',
      modal_compare_title: '// compare',
      compare_size: 'size',
      compare_delta: 'savings',
      compare_pct: '%',
      stats_files_label: 'files',
      stats_bytes_label: 'saved',
      stats_ratio_label: 'smaller',
      stats_chart_label: 'last 30 days',
      stats_chart_peak: 'peak · {n} files',
      stats_error: 'Failed to load statistics',
      // CR-056: expanded dashboard (collapsed view shows only the sparkline)
      stats_dash_title: 'service stats',
      stats_dash_tabs_24h: '24h',
      stats_dash_tabs_7d: '7d',
      stats_dash_tabs_30d: '30d',
      stats_dash_kpi_files: 'files',
      stats_dash_kpi_ok: 'ok',
      stats_dash_kpi_saved: 'saved',
      stats_dash_kpi_avg: 'avg ms',
      stats_dash_activity: 'activity',
      stats_dash_activity_legend_ok: 'ok',
      stats_dash_activity_legend_pending: 'pending',
      stats_dash_activity_legend_err: 'errors',
      stats_dash_top_formats: 'top formats',
      stats_dash_foot_peak: 'peak',
      stats_dash_foot_total: 'total',
      stats_dash_foot_updated: 'updated',
      stats_dash_format_total_label: 'total',
      stats_dash_format_success_label: 'ok',
      // DE-058: HEIC input x {webp,png,jpg} output pair labels
      // for the /api/stats/formats top-formats list (CR-059 consumer).
      stats_dash_format_heic_webp: 'HEIC → WEBP',
      stats_dash_format_heic_png:  'HEIC → PNG',
      stats_dash_format_heic_jpg:  'HEIC → JPG',
      // CR-057: collapsed/expanded toggle affordance labels.
      stats_band_expand_label: 'show details',
      stats_dash_close_label: 'collapse',
      // CR-053: per-cause notices (en) so the user can
      // distinguish a validation failure (400, the
      // request shape is wrong) from a backend outage
      // (503, stats subsystem is down) from a transport
      // failure (the fetch itself rejected; /api/stats
      // is not reachable, usually because the reverse-
      // proxy is not routing it).
      stats_error_validation: 'statistics: invalid query',
      stats_error_backend: 'statistics: backend unavailable',
      stats_error_network: 'statistics: request failed',
      // toasts
      toast_cap_hit: 'reached the 50-photo limit — clear some to add more',
      toast_skipped: 'skipped {n} file(s) — not a supported image format',
      toast_duplicate: 'skipped {n} duplicate file(s)',
      toast_no_image: 'not an image',
      toast_url_err: 'fetch failed',
      toast_url_cors: 'server blocks cross-origin reads',
      toast_url_ok: 'fetched · {size}',
      toast_invalid_url: 'not a valid http(s) URL',
      toast_decode: 'can\'t decode "{name}"',
      toast_too_big: 'file too large (max 10 MiB)',
      toast_network: 'network error',
      toast_no_session: 'session expired — files re-uploaded, try again',
      // pane hint (retired widget — kept for legacy strings)
      pane_hint: 'click to compare',
      pane_tag_before: 'before',
      pane_tag_after: 'after',
      // aria / a11y
      aria_drop: 'drop an image here or click to browse',
      aria_remove: 'remove from queue',
      aria_compare: 'compare before and after',
      aria_download: 'download this file',
      aria_dl_one: 'download this file',
      aria_remove_result: 'remove from results',
      aria_close: 'close compare',
      aria_format: 'output format',
      aria_pane_hint: 'click to compare',
      aria_share: 'share this page',
      // DE-058: HEIC chip-row tooltip strings (rendered when
      // caps[heic] is true; the "gated by the CLI build"
      // fallback stays hard-coded in renderFormatsChips for the
      // caps[heic] === false case).
      aria_heic_chip_tooltip_open: 'HEIC (iPhone container) — accepts .heic and .heif',
      aria_heif_chip_tooltip_open: 'HEIF — same container as HEIC',
      // url bar
      url_placeholder: 'paste an image URL…  (https://example.com/photo.jpg)',
      url_go: 'fetch',
      url_go_title: 'fetch and convert',
      url_hint: 'works on direct image links · server must allow cross-origin reads · tip:',
      url_hint_to_paste: 'to paste',
      // footer
      footer_brand: 'elrise',
      footer_brand_suffix: '.io',
      footer_text: 'tools/convert',
      footer_cookies: 'no cookies · optional self-hosted analytics · no upload to third parties',
      // misc
      busy: 'busy',
      done: 'done',
      failed: 'failed',
      queued: 'queued',
      // advanced panel (DE-030)
      advanced_label: 'advanced',
      advanced_hint: 'per-codec encoder options',
      advanced_empty: 'no advanced options for this format',
      advanced_unavailable: 'advanced options unavailable in this CLI build',
      // presets (DE-037)
      presets_label: 'preset',
      presets_web: 'web-for-web',
      presets_print: 'print',
      presets_archive: 'archive',
      presets_web_title: 'WebP for web · quality 82',
      presets_print_title: 'PNG lossless',
      presets_archive_title: 'AVIF for archival · quality 50',
      presets_web_aria: 'preset web for web',
      presets_print_aria: 'preset print',
      presets_archive_aria: 'preset archive',
      // AR-019 capability-driven transforms (DE-034)
      aria_resize_mode: 'resize mode',
      aria_resize_long_edge: 'long edge pixels',
      aria_rotate_arbitrary: 'rotate by arbitrary degrees',
      strip_exif_label: 'strip EXIF',
      strip_exif_always: 'EXIF metadata is always removed',
    },
    ru: {
      nav_privacy: 'приватность',
      nav_faq: 'faq',
      nav_repo: 'репозиторий',
      nav_how: 'как это работает',
      lang_en: 'EN',
      lang_ru: 'RU',
      // share (CR-046; CR-063: share_btn text removed; button is icon-only)
      share_copied: 'ссылка скопирована',
      share_failed: 'не удалось скопировать ссылку',
      hero_title: '.img converter',
      hero_sub: 'Перетащите одно или несколько изображений, нажмите Конвертировать, сравните, скачайте. <span class="accent">Ничего не загружается третьим лицам.</span>',
      // CR-082: the head-section strings (see i18n/ru.yaml) are
      // baked into the served HTML at image-build time by sitegen
      // (CR-073 / CR-079); see the EN block for the rationale.
      drop_title: 'перетащите сюда изображение',
      drop_sub: 'или кликните, чтобы выбрать · вставка из буфера тоже работает',
      drop_formats: 'png · jpg · webp · gif · bmp · avif · heic · heif · jxl',
      format_label: 'в',
      quality_label: 'качество — меньше = меньше файл, больше = лучше качество',
      trade_size: 'размер',
      trade_quality: 'качество',
      quality_desc_tiny: 'крошечный файл · видны артефакты',
      quality_desc_small: 'маленький файл · приемлемое качество',
      quality_desc_balanced: 'сбалансировано — хорошо для веба',
      quality_desc_high: 'высокое качество · умеренный размер',
      quality_desc_near: 'почти без потерь',
      quality_desc_lossless: 'визуально без потерь',
      tier_l: 'маленький',
      tier_b: 'сбалансировано',
      tier_h: 'высокое',
      tier_x: 'без потерь',
      resize_label: 'ресайз',
      resize_cap: 'ограничить размер',
      resize_axis_longest: 'длинная',
      resize_axis_width: 'ширина',
      resize_axis_height: 'высота',
      resize_axis_auto: 'авто (Г: ш · П: в)',
      resize_x: '×',
      resize_px: 'пикс',
      convert_btn: 'Конвертировать',
      convert_busy: 'Конвертация',
      convert_idle: 'Конвертация 0 из 0…',
      convert_progress: 'Конвертация {k} из {n}…',
      add_more: '+ добавить ещё',
      add_more_title: 'добавить ещё файлов',
      download_all: 'скачать всё (.zip)',
      staging_title: '// очередь',
      staging_progress: '{pending} в очереди',
      results_title: '// результаты',
      results_none: 'пока нет результатов — нажмите Конвертировать',
      results_clear: 'очистить результаты',
      expander_download: 'Скачать',
      expander_close: 'закрыть',
      expander_original: 'оригинал',
      expander_after: 'после',
      modal_compare_title: '// сравнение',
      compare_size: 'размер',
      compare_delta: 'экономия',
      compare_pct: '%',
      stats_files_label: 'файлов',
      stats_bytes_label: 'экономия',
      stats_ratio_label: 'меньше',
      stats_chart_label: 'за 30 дней',
      stats_chart_peak: 'пик · {n} файлов',
      stats_error: 'Не удалось загрузить статистику',
      // CR-056: expanded dashboard
      stats_dash_title: 'статистика сервиса',
      stats_dash_tabs_24h: '24 ч',
      stats_dash_tabs_7d: '7 д',
      stats_dash_tabs_30d: '30 д',
      stats_dash_kpi_files: 'файлов',
      stats_dash_kpi_ok: 'успешно',
      stats_dash_kpi_saved: 'экономия',
      stats_dash_kpi_avg: 'среднее мс',
      stats_dash_activity: 'активность по дням',
      stats_dash_activity_legend_ok: 'успешно',
      stats_dash_activity_legend_pending: 'в работе',
      stats_dash_activity_legend_err: 'ошибки',
      stats_dash_top_formats: 'топ форматов',
      stats_dash_foot_peak: 'пик',
      stats_dash_foot_total: 'всего',
      stats_dash_foot_updated: 'обновлено',
      stats_dash_format_total_label: 'всего',
      stats_dash_format_success_label: 'успешно',
      // DE-058: HEIC input x {webp,png,jpg} output pair labels
      // (mirror of the en table above).
      stats_dash_format_heic_webp: 'HEIC → WEBP',
      stats_dash_format_heic_png:  'HEIC → PNG',
      stats_dash_format_heic_jpg:  'HEIC → JPG',
      // CR-057: collapsed/expanded toggle affordance labels.
      stats_band_expand_label: 'подробнее',
      stats_dash_close_label: 'свернуть',
      // CR-053: per-cause notices mirroring the en keys.
      stats_error_validation: 'статистика: неверный запрос',
      stats_error_backend: 'статистика: бэкенд недоступен',
      stats_error_network: 'статистика: запрос не выполнен',
      toast_cap_hit: 'достигнут лимит 50 фото — очистите, чтобы добавить ещё',
      toast_skipped: 'пропущено {n} файл(ов) — неподдерживаемый формат',
      toast_duplicate: 'пропущено {n} дубликат(ов)',
      toast_no_image: 'это не изображение',
      toast_url_err: 'загрузка не удалась',
      toast_url_cors: 'сервер блокирует кросс-доменные запросы',
      toast_url_ok: 'загружено · {size}',
      toast_invalid_url: 'некорректный http(s) URL',
      toast_decode: 'не удаётся декодировать «{name}»',
      toast_too_big: 'файл слишком большой (макс. 10 МиБ)',
      toast_network: 'ошибка сети',
      toast_no_session: 'сессия истекла — файлы перезагружены, попробуйте ещё раз',
      pane_hint: 'нажмите, чтобы сравнить',
      pane_tag_before: 'до',
      pane_tag_after: 'после',
      aria_drop: 'перетащите изображение сюда или кликните, чтобы выбрать',
      aria_remove: 'убрать из очереди',
      aria_compare: 'сравнить до и после',
      aria_download: 'скачать этот файл',
      aria_dl_one: 'скачать этот файл',
      aria_remove_result: 'убрать из результатов',
      aria_close: 'закрыть сравнение',
      aria_format: 'выходной формат',
      aria_pane_hint: 'нажмите, чтобы сравнить',
      aria_share: 'поделиться страницей',
      // DE-058: HEIC chip-row tooltip strings (mirror of the en
      // table above; rendered when caps[heic] is true).
      aria_heic_chip_tooltip_open: 'HEIC (контейнер iPhone) — принимает .heic и .heif',
      aria_heif_chip_tooltip_open: 'HEIF — тот же контейнер, что и HEIC',
      url_placeholder: 'вставьте URL изображения…  (https://example.com/photo.jpg)',
      url_go: 'загрузить',
      url_go_title: 'загрузить и конвертировать',
      url_hint: 'работает с прямыми ссылками · сервер должен разрешать кросс-доменные запросы · подсказка:',
      url_hint_to_paste: 'чтобы вставить',
      footer_brand: 'elrise',
      footer_brand_suffix: '.io',
      footer_text: 'tools/convert',
      footer_cookies: 'без cookies · опциональная self-hosted аналитика · без загрузки третьим лицам',
      busy: 'обработка',
      done: 'готово',
      failed: 'ошибка',
      queued: 'в очереди',
      // advanced panel (DE-030)
      advanced_label: 'расширенные',
      advanced_hint: 'параметры кодека',
      advanced_empty: 'нет расширенных параметров для этого формата',
      advanced_unavailable: 'расширенные параметры недоступны',
      // presets (DE-037)
      presets_label: 'пресет',
      presets_web: 'веб-для-веба',
      presets_print: 'печать',
      presets_archive: 'архив',
      presets_web_title: 'WebP для веба · качество 82',
      presets_print_title: 'PNG без потерь',
      presets_archive_title: 'AVIF для архива · качество 50',
      presets_web_aria: 'пресет web для веба',
      presets_print_aria: 'пресет print',
      presets_archive_aria: 'пресет archive',
      // AR-019 capability-driven transforms (DE-034)
      aria_resize_mode: 'режим ресайза',
      aria_resize_long_edge: 'длинная сторона, пикс',
      aria_rotate_arbitrary: 'поворот на произвольный угол',
      strip_exif_label: 'удалить EXIF',
      strip_exif_always: 'Метаданные EXIF всегда удаляются',
    },
  };

  const t = (key, vars) => {
    const s = (FORM_I18N[locale] && FORM_I18N[locale][key]) || FORM_I18N.en[key] || key;
    if (!vars) return s;
    return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : '{' + k + '}'));
  };

  // Locale bootstrap; persisted in localStorage. Default 'en'.
  // When the page is loaded at a locale URL (e.g. http://127.0.0.1:8095/
  // for the RU port), <html data-locale-default="ru"> seeds the initial
  // locale before any JS click, so crawlers that disable JS see the
  // right content and JS users see the right CTA copy on first paint.
  const scriptLocale = document.currentScript?.dataset?.defaultLocale;
  const documentLocale = document.documentElement.getAttribute('data-locale-default');
  // Parse the runtime locale + per-domain config filled by the
  // Dockerfile htmlsubst stage (DE-071). When SITE_DEFAULT_LOCALE is
  // unset the placeholder is preserved verbatim and siteConfig stays
  // null; the bootstrap then falls back to the static data-* markers.
  const siteConfig = (() => {
    try {
      const el = document.getElementById('site-config');
      if (!el || !el.textContent) return null;
      const raw = el.textContent.trim();
      if (!raw || raw === '__SITE_CONFIG__') return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (_) { return null; }
  })();
  if (siteConfig) window.__SITE_CONFIG__ = siteConfig;
  const runtimeDefault = siteConfig && (siteConfig.default_locale === 'en' || siteConfig.default_locale === 'ru')
    ? siteConfig.default_locale
    : null;
  const defaultLocale = runtimeDefault
    || (scriptLocale === 'en' || scriptLocale === 'ru'
        ? scriptLocale
        : (documentLocale === 'en' || documentLocale === 'ru' ? documentLocale : 'en'));
  let locale = defaultLocale;
  const LS_KEY = 'locale';
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved === 'en' || saved === 'ru') locale = saved;
  } catch (_) { /* localStorage may be unavailable; keep the page default */ }

  const setLocale = (code) => {
    if (code !== 'en' && code !== 'ru') return;
    locale = code;
    try { localStorage.setItem(LS_KEY, code); } catch (_) { /* ignore */ }
    document.documentElement.lang = code;
    // sync pill press state
    const enBtn = document.getElementById('lang-en');
    const ruBtn = document.getElementById('lang-ru');
    if (enBtn) enBtn.setAttribute('aria-pressed', code === 'en' ? 'true' : 'false');
    if (ruBtn) ruBtn.setAttribute('aria-pressed', code === 'ru' ? 'true' : 'false');
    // sync lang-pill href to the runtime config's per-domain URL so
    // right-click "open in new tab" lands on the locale's domain.
    if (siteConfig && siteConfig.domains) {
      if (enBtn && siteConfig.domains.en) enBtn.setAttribute('href', siteConfig.domains.en);
      if (ruBtn && siteConfig.domains.ru) ruBtn.setAttribute('href', siteConfig.domains.ru);
    }
    // CR-081: <title>, <meta description>, og:*, twitter:*, and
    // og:locale are emitted at image-build time by sitegen (CR-073
    // / CR-079). The static HTML carries the per-locale values
    // already; the runtime no longer mutates them. SEO crawlers
    // and social-share preview scrapers see the right values
    // before JS runs. renderI18n() below still runs because
    // capability-driven form copy (DE-058 / CR-058) and the
    // data-i18n body strings depend on it.
    renderI18n();
    // dynamic strings that aren't in [data-i18n] / [data-i18n-attr]
    renderConvertBtn();
    if (progressVisible) renderConvertProgress();
    renderResultsCount();
    renderStagingCount();
    renderStatsFromCache();
    // aria-labels that are computed (e.g. per-tile)
    document.querySelectorAll('.results-tile').forEach((el) => {
      const itId = el.dataset.id;
      const it = items.find((x) => x.id === itId);
      if (it) el.setAttribute('aria-label', t('aria_compare'));
    });
  };

  // Progressive enhancement on the lang-pill anchors. The bare <a
  // href="..."> stays for keyboard / screen-reader access (and for
  // crawlers that disable JS); the click handler intercepts user
  // activation and either redirects to the per-domain URL (operator's
  // reverse proxy routes per-host) or falls back to setLocale(code)
  // when both locales share a domain or the runtime config is
  // missing. href is updated by setLocale() so right-click "open in
  // new tab" lands on the correct domain too.
  const wireLangPill = () => {
    const onClick = (e) => {
      const target = e.currentTarget;
      const targetLocale = target && target.dataset && target.dataset.locale;
      if (targetLocale !== 'en' && targetLocale !== 'ru') return;
      const cfg = window.__SITE_CONFIG__;
      if (cfg && cfg.domains && cfg.domains[targetLocale]) {
        const targetUrl = cfg.domains[targetLocale];
        let here = '';
        try { here = window.location.origin; } catch (_) { here = ''; }
        if (targetUrl && targetUrl !== here && targetUrl !== here + '/') {
          e.preventDefault();
          window.location.href = targetUrl;
          return;
        }
      }
      e.preventDefault();
      setLocale(targetLocale);
    };
    const enBtn = document.getElementById('lang-en');
    const ruBtn = document.getElementById('lang-ru');
    if (enBtn) enBtn.addEventListener('click', onClick);
    if (ruBtn) ruBtn.addEventListener('click', onClick);
  };

  const renderI18n = () => {
    document.querySelectorAll('[data-i18n]').forEach((el) => {
      const key = el.getAttribute('data-i18n');
      if (key) el.innerHTML = t(key);
    });
    document.querySelectorAll('[data-i18n-attr]').forEach((el) => {
      const attr = el.getAttribute('data-i18n-attr');
      const key = el.getAttribute('data-key');
      if (attr && key) el.setAttribute(attr, t(key));
    });
    // composite aria-label pairs (data-i18n-attr + data-i18n-attr-2)
    document.querySelectorAll('[data-i18n-attr-2]').forEach((el) => {
      const attr2 = el.getAttribute('data-i18n-attr-2');
      const key2 = el.getAttribute('data-key-2');
      if (attr2 && key2) el.setAttribute(attr2, t(key2));
    });
  };

  // ---- DOM ----
  const $ = (s) => document.querySelector(s);
  const dz         = $('#dropzone');
  const fileInput  = $('#file');
  const addmoreBtn = $('#addmore');

  const formatSel  = $('#format');
  const quality    = $('#quality');
  const barSize    = $('#bar-size');
  const barQuality = $('#bar-quality');
  const bubble     = $('#quality-bubble');
  const descEl     = $('#quality-desc');
  const tierEls    = { lossy: $('#tier-lossy'), balanced: $('#tier-balanced'), high: $('#tier-high'), lossless: $('#tier-lossless') };
  const resizeOn   = $('#resize-on');
  const resizeMax  = $('#resize-max');
  const resizeAxis = $('#resize-axis');
  const resizeW    = $('#resize-w');
  const resizeH    = $('#resize-h');
  const resizeMaxSuffix  = $('#resize-max-suffix');
  const resizeAxisOnlyEls = document.querySelectorAll('.resize-axis-only');

  const convertBtn     = $('#convert-btn');
  const convertBtnLbl  = $('#convert-btn-label');
  const convertProg    = $('#convert-progress');
  const convertProgFill= $('#convert-progress-fill');
  const convertProgText= $('#convert-progress-text');
  const saveBadge      = $('#save-badge');
  const advAlpha       = $('#adv-alpha');
  const toast          = $('#toast');

  // staging
  const dropzoneCopy = $('#dropzone-copy');
  const staging      = $('#staging');
  const stagingList  = $('#staging-list');
  const stagingCount = $('#staging-count');
  const stagingTotal = $('#staging-total');

  // results
  const results      = $('#results');
  const resultsList  = $('#results-list');
  const resultsCount = $('#results-count');
  const resultsTotal = $('#results-total');
  const resultsEmpty = $('#results-empty');
  const resultsClear = $('#results-clear');
  const resultsDlAll = $('#results-dl-all');

  // modal compare
  const compareModal = $('#compare-modal');
  const baSlider     = $('#ba-slider');
  const baBefore     = $('#ba-before');
  const baAfter      = $('#ba-after');
  const baHandle     = $('#ba-handle');
  const baBeforeSize = $('#ba-before-size');
  const baAfterSize  = $('#ba-after-size');
  const baAfterLabel = $('#ba-after-label');
  const baDelta      = $('#ba-delta');
  const baName       = $('#ba-name');
  const baDownload   = $('#ba-download');

  // URL form
  const urlForm    = $('#url');
  const urlInput   = $('#url-input');
  const urlGo      = $('#url-go');
  const urlStatus  = $('#url-status');

  // ---- state ----
  const MAX_QUEUE = 50;
  const BATCH = 6;
  const items = [];           // every accepted file: pending | busy | done | err
  let openCompareId = null;   // modal-tracked item id
  let baCompareItem = null;  // item object held by the modal
  let poolRunning = false;
  let progressVisible = false;
  // CR-050: per-session id for the server-side session
  // store. The id is allocated by the server (POST
  // /api/sessions returns {id}) so the SPA does NOT
  // need to write to sessionStorage / localStorage.
  // The id lives in this IIFE-scoped variable only;
  // a page reload gets a fresh id from the server
  // (CR-051 will eventually allow reusing the same id
  // for a reload inside a short window; that is a
  // separate deliverable).
  let currentSessionId = null;
  // CR-050: session-id bootstrap. Asks the server for a
  // fresh id; if the request fails, returns null and
  // the SPA falls back to local-conversion-only (no
  // archive from disk; CR-051 will then no-op on the
  // second-click path until the session store is back).
  // Defined early so the IIFE entry path can call it
  // without TDZ violations.
  const ensureSessionId = async () => {
    if (currentSessionId) return currentSessionId;
    try {
      const r = await fetch('/api/sessions', { method: 'POST' });
      if (!r.ok) return null;
      const body = await r.json();
      if (body && typeof body.id === 'string' && /^sess_[a-z0-9-]{36}$/.test(body.id)) {
        currentSessionId = body.id;
        return body.id;
      }
      return null;
    } catch (_) {
      return null;
    }
  };
  document.documentElement.lang = locale;
  stagingTotal.textContent = MAX_QUEUE;
  // CR-050: initialise the per-session id at IIFE entry
  // so the first upload hook (called from the
  // post-encode path inside runConvertQueue) does not
  // race the bootstrap read. The await is fire-and-
  // forget; uploadEncodedToSession awaits ensureSessionId
  // internally before the fetch.
  ensureSessionId();

  // ---- helpers ----
  const fmt = (n) => {
    if (n == null || isNaN(n)) return '–';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / 1024 / 1024).toFixed(2) + ' MB';
  };
  const pct = (a, b) => (b > 0 ? Math.round(((a - b) / a) * 100) : 0);
  const id = () => Math.random().toString(36).slice(2, 10);
  const extFor = (m) => ({'image/webp':'webp','image/png':'png','image/jpeg':'jpg'})[m] || 'bin';

  const useQ = (m) => m === 'image/webp' || m === 'image/jpeg';
  const targetMime = () => formatSel.value;

  const showSlider = () => {
    const lossy = useQ(targetMime());
    document.querySelectorAll('.q-inline, .trade').forEach((el) => {
      el.style.display = lossy ? '' : 'none';
    });
  };

  // ---- toast ----
  let toastTimer = null;
  const showToast = (msg) => {
    toast.textContent = msg;
    toast.classList.add('is-shown');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-shown'), 3200);
  };

  // ---- format / quality ----
  // Format/quality change: invalidate both pending AND done items so the
  // explicit Convert button re-encodes the entire queue.
  const invalidateAll = () => {
    items.forEach((it) => {
      if (it.serverUrl) URL.revokeObjectURL(it.serverUrl);
      it.serverBlob = null;
      it.serverUrl = null;
      it.status = 'pending';
    });
  };
  const onFormatChange = () => {
    showSlider();
    invalidateAll();
    renderStaging(); renderResults(); renderConvertBtn();
    renderAdvancedPanel();
  };
  formatSel?.addEventListener('change', onFormatChange);

  // ---- advanced panel: per-codec X-Encoder-Options inputs (DE-030) ----
  // The backend DE-026..DE-029 define the canonical keys
  // (the allowlist in backend/cmd/converter-server/encoder_options.go
  // is the source of truth); the page mirrors them. Adding
  // a new key is a backend allowlist row + this mirror;
  // the page never invents keys. When the user expands the
  // panel and changes a value, the next convert() call sends
  // the matching X-Encoder-Options header. When the panel is
  // collapsed (default state) and no input is touched, the
  // header is not sent (byte-identical pre-DE-030 baseline;
  // AC-DE-030-4).
  const ENCODER_OPTIONS_BY_CODEC = {
    'image/webp': [
      { key: 'target_size',      kind: 'int',  hint: '1..100 MiB' },
      { key: 'sns',              kind: 'int',  hint: '0..100 (default 50)' },
      { key: 'filter_sharpness', kind: 'int',  hint: '0..7 (default 0)' },
      { key: 'pass',             kind: 'int',  hint: '1..10 (default 1)' },
    ],
    'image/jpeg': [
      { key: 'subsampling', kind: 'enum', values: ['4:4:4', '4:2:2', '4:2:0'], hint: 'default 4:2:0' },
      // MozJPEG keys (`optimize_cru`, `trellis_ac`) are
      // gated until AR-021 (DE-026 allowlist registers them
      // with RequiresMozJPEG: true; the backend rejects
      // them with 400 mozjpeg_encoder_not_available until
      // the post-AR-021 developer task flips MozJPEGAvailable
      // to true). Hidden by design (AC-DE-030-6: "hidden,
      // not advertised").
    ],
    'image/png': [
      { key: 'compression_level', kind: 'int', hint: '0..9 (default 6)' },
    ],
    'image/avif': [
      { key: 'speed', kind: 'int',  hint: '0..10 (default 6)' },
      { key: 'tune',  kind: 'enum', values: ['psnr', 'ssim'], hint: 'default psnr' },
    ],
  };

  const advancedPanel = document.getElementById('advanced-panel');
  const advancedBody = document.getElementById('advanced-body');
  const advancedEmpty = document.getElementById('advanced-empty');
  const advancedUnavailable = document.getElementById('advanced-unavailable');

  const renderAdvancedPanel = () => {
    if (!advancedBody || !advancedEmpty || !advancedUnavailable) return;
    const fmt = formatSel.value;
    // Capability gating per AC-DE-030-7: AVIF inputs are
    // rendered only when formatsMatrix.capabilities.avif
    // is true (or the matrix is unavailable / pre-fetch).
    // When the matrix reports avif: false, the AVIF block
    // is replaced with the documented one-line note
    // "AVIF encoder unavailable in this CLI build".
    const caps = (formatsMatrix && formatsMatrix.capabilities) || {};
    if (fmt === 'image/avif' && caps['avif'] === false) {
      advancedBody.innerHTML = '';
      advancedBody.classList.add('is-hidden');
      advancedEmpty.classList.add('is-hidden');
      advancedUnavailable.classList.remove('is-hidden');
      return;
    }
    const keys = ENCODER_OPTIONS_BY_CODEC[fmt] || [];
    advancedUnavailable.classList.add('is-hidden');
    if (keys.length === 0) {
      advancedBody.innerHTML = '';
      advancedBody.classList.add('is-hidden');
      advancedEmpty.classList.remove('is-hidden');
      return;
    }
    advancedEmpty.classList.add('is-hidden');
    advancedBody.classList.remove('is-hidden');
    advancedBody.innerHTML = '';
    for (const opt of keys) {
      const row = document.createElement('div');
      row.className = 'advanced__row';
      const lbl = document.createElement('span');
      lbl.className = 'advanced__key';
      lbl.textContent = opt.key;
      row.appendChild(lbl);
      let input;
      if (opt.kind === 'enum') {
        input = document.createElement('select');
        input.className = 'input input--select';
        // The empty option is the "default" — backend
        // applies its own CLI default when the key is
        // absent; the byte-identical baseline is preserved.
        const blank = document.createElement('option');
        blank.value = '';
        blank.textContent = '—';
        input.appendChild(blank);
        for (const v of opt.values) {
          const o = document.createElement('option');
          o.value = v;
          o.textContent = v;
          input.appendChild(o);
        }
      } else {
        input = document.createElement('input');
        input.type = 'number';
        input.className = 'input input--num';
        input.placeholder = opt.hint || '';
      }
      input.dataset.key = opt.key;
      input.id = 'enc-opt-' + opt.key;
      row.appendChild(input);
      if (opt.hint) {
        const hint = document.createElement('span');
        hint.className = 'advanced__hint';
        hint.textContent = opt.hint;
        row.appendChild(hint);
      }
      advancedBody.appendChild(row);
    }
  };

  // getEncoderOptions returns the comma-separated key=value
  // list for the X-Encoder-Options header, or empty string
  // when the panel is collapsed and no input has been touched
  // (the byte-identical baseline for the pre-DE-030 path).
  // Empty-string inputs are omitted (the backend parser
  // rejects empty values per DE-026 allowlist). The
  // accessor is the single source of truth shared between
  // the panel and the convert submit path (AC-DE-030-3).
  const getEncoderOptions = () => {
    if (!advancedPanel || !advancedPanel.open) return '';
    if (!advancedBody) return '';
    const parts = [];
    for (const el of advancedBody.querySelectorAll('[data-key]')) {
      const v = (el.value || '').trim();
      if (!v) continue;
      parts.push(el.dataset.key + '=' + v);
    }
    return parts.join(',');
  };

  // ---- format presets (DE-037) ----
  // The three preset values are pinned by
  // docs/contracts/presets.md. Selecting a preset populates
  // the existing format <select>, quality slider, and
  // advanced-panel encoder-option inputs in one action.
  // The preset is NOT a lock (AC-DE-037-3): the user can
  // fine-tune any field afterwards.
  const PRESETS = {
    'web-for-web': {
      format: 'image/webp',
      quality: 82,
      encOpts: { method: 6, sns: 50, pass: 1 },
    },
    'print': {
      format: 'image/png',
      quality: 100,
      encOpts: {},
    },
    'archive': {
      format: 'image/avif',
      quality: 50,
      encOpts: { speed: 6, tune: 'ssim' },
    },
  };

  // renderPresets shows/hides the archive button based on
  // the AVIF capability from /api/formats. web-for-web and
  // print are always rendered (WebP + PNG are core formats;
  // AC-DE-037-4).
  const renderPresets = () => {
    const caps = (formatsMatrix && formatsMatrix.capabilities) || {};
    const archiveBtn = document.querySelector('.preset--archive');
    if (!archiveBtn) return;
    if (caps['avif'] === false) {
      archiveBtn.classList.add('is-hidden');
      archiveBtn.setAttribute('aria-hidden', 'true');
    } else {
      archiveBtn.classList.remove('is-hidden');
      archiveBtn.removeAttribute('aria-hidden');
    }
  };

  // setPreset populates the format select, quality slider,
  // and advanced-panel encoder-option inputs from a preset.
  // The function opens the advanced panel when the preset
  // specifies encoder options, so the user sees the
  // populated fields. The convertOnServer submit path
  // reads the populated values via the existing
  // `getEncoderOptions()` accessor (no new header surface).
  const setPreset = (name) => {
    const p = PRESETS[name];
    if (!p) return;
    // Update the format <select>; renderFormats() must be
    // called first to ensure the target option exists
    // (formatsMatrix may have narrowed the list).
    renderFormats();
    if (p.format) formatSel.value = p.format;
    // Update the quality slider.
    quality.value = String(p.quality);
    updateQ();
    // Open the advanced panel if the preset specifies
    // encoder options, then populate the inputs. The
    // panel's `change` event re-renders the inputs based
    // on the new format.
    if (advancedPanel) {
      advancedPanel.open = true;
      renderAdvancedPanel();
    }
    for (const opt of ENCODER_OPTIONS_BY_CODEC[p.format] || []) {
      const input = advancedBody && advancedBody.querySelector('[data-key="' + opt.key + '"]');
      if (!input) continue;
      const v = p.encOpts[opt.key];
      input.value = v == null ? '' : String(v);
    }
    // Mirror the populate actions in the page state
    // machine: invalidating all pending items mirrors
    // onFormatChange so the next Convert click re-encodes
    // with the new settings (the pre-wave behaviour; no
    // silent forward of stale items).
    invalidateAll();
    renderStaging(); renderResults(); renderConvertBtn();
  };

  // Wire the preset buttons.
  document.querySelectorAll('.preset[data-preset]').forEach((btn) => {
    btn.addEventListener('click', () => setPreset(btn.dataset.preset));
  });

  // ---- capability-driven transforms (DE-034) ----
  // The page fetches GET /api/capabilities once per page
  // load and binds the AR-019 resize / EXIF / rotate
  // controls to the capability surface. When the matrix
  // reports only "none" for resize, the resize block is
  // replaced with the documented one-line note; the
  // arbitrary-degree slider is hidden when the matrix
  // reports rotate.arbitrary=false; the strip-exif toggle
  // is hidden when exif.strip=false.
  let capabilitiesMatrix = null;
  // CR-116: shared "matrix reports only the 'none' resize mode"
  // flag. `renderCapabilities` updates it; `applyResizeBarVisibility`
  // reads it. Default true so the conservative first render hides the
  // pair-2 controls until /api/capabilities resolves.
  let onlyNoneResize = true;
  const ar019Block = document.getElementById('ar019-block');
  const ar019RotateBlock = document.getElementById('ar019-rotate-block');
  const stripExifRow = document.getElementById('strip-exif-row');
  const stripExifNote = document.getElementById('strip-exif-note');
  const resizeModeSelect = document.getElementById('resize-mode');
  const resizeModeLongEdge = document.getElementById('resize-mode-long-edge');
  const rotateArbitraryInput = document.getElementById('rotate-arbitrary');

  // CR-116: pair-2 visibility helper. The `mode` + `long edge` controls
  // must follow the `cap dimension` checkbox AND the capability-matrix
  // gate, composed with AND. Replaces the prior display-style toggles
  // on the inner `resize-mode` / `resize-mode-long-edge` elements.
  const applyResizeBarVisibility = () => {
    if (!ar019Block) return;
    const on = !!resizeOn?.checked && !onlyNoneResize;
    ar019Block.style.display = on ? '' : 'none';
  };

  const renderCapabilities = () => {
    const resize = (capabilitiesMatrix && capabilitiesMatrix.resize) || { modes: ['none'], max_long_edge: 0 };
    const exif = (capabilitiesMatrix && capabilitiesMatrix.exif) || { auto_orient: false, strip: false };
    const rotate = (capabilitiesMatrix && capabilitiesMatrix.rotate) || { presets: [], arbitrary: false };
    // Resize modes. When the only mode is "none", the
    // resize block is replaced with the documented note.
    onlyNoneResize = !resize.modes || resize.modes.length === 0 || (resize.modes.length === 1 && resize.modes[0] === 'none');
    if (resizeModeSelect) {
      resizeModeSelect.innerHTML = '';
      if (onlyNoneResize) {
        // Render a single disabled "none" option so the
        // select still has a stable value when the matrix
        // reports no modes.
        const o = document.createElement('option');
        o.value = 'none';
        o.textContent = 'none';
        resizeModeSelect.appendChild(o);
        resizeModeSelect.value = 'none';
        resizeModeSelect.disabled = true;
      } else {
        for (const m of resize.modes) {
          const o = document.createElement('option');
          o.value = m;
          o.textContent = m;
          resizeModeSelect.appendChild(o);
        }
        resizeModeSelect.value = 'contain';
        resizeModeSelect.disabled = false;
      }
    }
    if (ar019Block) {
      // Bind the long-edge input's max attribute to the
      // matrix's max_long_edge (0 means unset).
      if (resizeModeLongEdge) {
        if (resize.max_long_edge > 0) {
          resizeModeLongEdge.max = String(resize.max_long_edge);
        }
      }
      applyResizeBarVisibility();
    }
    // Rotate arbitrary slider. Hidden when the matrix
    // reports rotate.arbitrary=false (the preset buttons
    // remain visible regardless).
    if (rotateArbitraryInput) {
      rotateArbitraryInput.style.display = rotate.arbitrary ? '' : 'none';
    }
    // EXIF strip. `exif.strip` reports whether the CLI exposes a
    // --strip-exif *flag*, not whether it can remove metadata.
    // Builds without the flag strip unconditionally (they decode to
    // pixels and re-encode), so hiding the row on its own read as
    // "this feature is gone" — CR-065. Show the toggle when the flag
    // exists and a choice is therefore meaningful; otherwise state
    // that removal always happens.
    if (stripExifRow) {
      stripExifRow.style.display = exif.strip ? '' : 'none';
    }
    if (stripExifNote) {
      stripExifNote.classList.toggle('is-hidden', !!exif.strip);
    }
  };

  const loadCapabilitiesMatrix = () => {
    if (typeof window === 'undefined' || !window.fetch) return;
    fetch('/api/capabilities', { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((data) => {
        if (data && data.resize && data.exif && data.rotate && typeof data.cli_version === 'string') {
          capabilitiesMatrix = data;
          renderCapabilities();
        }
      })
      .catch(() => { /* conservative matrix (set by the renderer default) */ });
  };

  // Initial render (with conservative matrix); the fetch
  // populates the real matrix and re-renders on success.
  renderCapabilities();
  loadCapabilitiesMatrix();

  // tier mapping: 1-30 small, 31-65 balanced, 66-90 high, 91-100 lossless
  const tierFor = (v) => v <= 30 ? 'lossy' : v <= 65 ? 'balanced' : v <= 90 ? 'high' : 'lossless';
  const tierDescKey = (v) => {
    if (v <= 30) return 'quality_desc_tiny';
    if (v <= 50) return 'quality_desc_small';
    if (v <= 70) return 'quality_desc_balanced';
    if (v <= 85) return 'quality_desc_high';
    if (v <= 95) return 'quality_desc_near';
    return 'quality_desc_lossless';
  };
  const updateQ = () => {
    const v = Number(quality.value);
    if (barSize)    barSize.style.width    = (100 - v) + '%';
    if (barQuality) barQuality.style.width = v + '%';
    if (quality)    quality.style.setProperty('--val', v + '%');
    if (bubble) {
      bubble.textContent = v;
      bubble.style.left = v + '%';
    }
    if (descEl)     descEl.innerHTML        = t(tierDescKey(v));
    const tier = tierFor(v);
    Object.entries(tierEls).forEach(([k, el]) => {
      if (!el) return;
      el.classList.toggle('is-active', k === tier);
      el.setAttribute('aria-pressed', k === tier ? 'true' : 'false');
    });
  };
  const TIER_MIDPOINTS = { lossy: 25, balanced: 65, high: 82, lossless: 100 };
  const setTier = (key) => {
    const v = TIER_MIDPOINTS[key];
    if (v == null || !quality) return;
    quality.value = String(v);
    updateQ();
    quality.focus();
  };
  Object.entries(tierEls).forEach(([k, el]) => {
    if (!el) return;
    el.addEventListener('click', () => setTier(k));
    el.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      setTier(k);
    });
  });
  let qTimer = null;
  const onQInput = () => {
    // Quality slider motion only re-paints the trade widget. Done
    // items keep their encoded blob and stay visible in the result
    // strip; re-encoding happens only on the explicit Convert click.
    updateQ();
    clearTimeout(qTimer);
  };
  quality?.addEventListener('input', onQInput);
  updateQ();

  advAlpha?.addEventListener('change', () => {
    invalidateAll();
    renderStaging(); renderResults(); renderConvertBtn();
  });

  // resize: toggle enabled state + invalidate on change so re-encode
  // happens on the next explicit Convert click (mirrors the quality
  // slider / format select rules). Axis is one of 'longest' |
  // 'width' | 'height' | 'auto'. For 'auto' the user supplies two
  // caps: a width cap applied to landscape images (w >= h) and a
  // height cap applied to portrait images (h > w). 'auto' is the
  // CLI's 'auto:portrait=H,landscape=W' policy; we honour it on the
  // client side so 'height' cap works for portrait images regardless
  // of the convert-to-webp CLI's --resize policy.
  const resizeSpec = () => {
    if (!resizeOn?.checked) return null;
    const axis = resizeAxis?.value || 'longest';
    if (axis === 'auto') {
      const w = Math.max(64, Math.min(20000, Number(resizeW?.value) || 1600));
      const h = Math.max(64, Math.min(20000, Number(resizeH?.value) || 2400));
      return { mode: 'auto', w, h };
    }
    return { mode: 'one', max: Math.max(64, Math.min(20000, Number(resizeMax?.value) || 1600)), axis };
  };
  const syncResizeUi = () => {
    const on = !!resizeOn?.checked;
    const axis = resizeAxis?.value || 'longest';
    const isAuto = axis === 'auto';
    if (resizeMax)  resizeMax.disabled  = !on || isAuto;
    if (resizeAxis) resizeAxis.disabled = !on;
    if (resizeW)    resizeW.disabled    = !on || !isAuto;
    if (resizeH)    resizeH.disabled    = !on || !isAuto;
    resizeAxisOnlyEls.forEach((el) => {
      el.hidden = !isAuto;
      el.classList.toggle('is-shown', isAuto);
    });
    // CR-116: re-evaluate the pair-2 visibility gate so a
    // checkbox toggle re-applies it without waiting on a
    // capabilities re-fetch.
    applyResizeBarVisibility();
  };
  const onResizeChange = () => {
    syncResizeUi();
    invalidateAll();
    renderStaging(); renderResults(); renderConvertBtn();
  };
  resizeOn?.addEventListener('change', onResizeChange);
  resizeAxis?.addEventListener('change', onResizeChange);
  resizeMax?.addEventListener('input', onResizeChange);
  resizeW?.addEventListener('input', onResizeChange);
  resizeH?.addEventListener('input', onResizeChange);
  syncResizeUi();

  // ---- file ingestion ----
  const ACCEPT = /^image\//;
  const isImage = (f) => f.type
    ? ACCEPT.test(f.type)
    : /\.(png|jpe?g|webp|gif|bmp|tiff?|heic|heif|avif)$/i.test(f.name);

  let direction = 'to-webp';
  const MIME_FROM_SHORT = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', bmp: 'image/bmp', avif: 'image/avif', heic: 'image/heic', heif: 'image/heif', jxl: 'image/jxl' };
  const SHORT_FROM_MIME = { 'image/png': 'png', 'image/jpeg': 'jpeg', 'image/webp': 'webp', 'image/gif': 'gif', 'image/bmp': 'bmp', 'image/avif': 'avif', 'image/heic': 'heic', 'image/heif': 'heif', 'image/jxl': 'jxl' };
  const LABEL_FROM_MIME = { 'image/png': 'PNG', 'image/jpeg': 'JPG', 'image/webp': 'WebP', 'image/gif': 'GIF', 'image/bmp': 'BMP', 'image/avif': 'AVIF', 'image/heic': 'HEIC', 'image/heif': 'HEIF', 'image/jxl': 'JXL' };
  // core formats are always available; the conservative matrix
  // carries them too. The capability matrix from /api/formats
  // extends the list when the CLI build supports additional codecs.
  const CORE_OUTPUTS = ['image/webp', 'image/png', 'image/jpeg'];
  const CORE_INPUTS = ['png', 'jpg', 'jpeg', 'webp'];

  let formatsMatrix = null;
  let formatsFailed = false;

  const renderFormats = () => {
    const outs = formatsMatrix && formatsMatrix.outputs && formatsMatrix.outputs.length
      ? formatsMatrix.outputs.map((s) => MIME_FROM_SHORT[s] || `image/${s}`)
      : CORE_OUTPUTS;
    // CR-009: formatsMatrix.outputs carries both `jpg` and `jpeg`
    // short tokens; both map to `image/jpeg`. Dedup the list so
    // the dropdown renders one `JPG` row, not two. The wire API
    // still accepts both spellings (docs/contracts/convert-api.md).
    const uniq = Array.from(new Set(outs));
    const list = direction === 'to-webp' ? uniq : uniq.filter((m) => m !== 'image/webp');
    formatSel.innerHTML = '';
    for (const m of list) {
      const o = document.createElement('option');
      o.value = m;
      o.textContent = LABEL_FROM_MIME[m] || m;
      formatSel.appendChild(o);
    }
    const preferred = direction === 'to-webp' ? 'image/webp' : 'image/png';
    formatSel.value = list.includes(preferred) ? preferred : list[0];
    showSlider();
    // DE-030: rebuild the per-codec X-Encoder-Options
    // inputs whenever the format list changes (initial
    // load + post-capability-fetch). The on-change
    // handler also calls renderAdvancedPanel so user-
    // driven format switches are covered.
    renderAdvancedPanel();
  };

  const renderFormatsChips = () => {
    const row = document.getElementById('dropzone-formats');
    if (!row) return;
    row.innerHTML = '';
    const ins = formatsMatrix && formatsMatrix.inputs && formatsMatrix.inputs.length
      ? formatsMatrix.inputs
      : CORE_INPUTS;
    const caps = (formatsMatrix && formatsMatrix.capabilities) || {};
    for (const s of ins) {
      const span = document.createElement('span');
      span.textContent = s;
      if (s.length === 4 && s === 'heic') {
        span.title = caps['heic']
          ? t('aria_heic_chip_tooltip_open')
          : 'HEIC (iPhone container) — gated by the CLI build';
      } else if (s === 'heif') {
        span.title = caps['heic']
          ? t('aria_heif_chip_tooltip_open')
          : 'HEIF — gated by the CLI build';
      } else if (s === 'jxl') {
        span.title = 'JPEG XL — gated by the CLI build';
      }
      if (CORE_INPUTS.indexOf(s) === -1 && caps[s] === false) {
        span.classList.add('is-disabled');
        span.setAttribute('aria-disabled', 'true');
      }
      row.appendChild(span);
    }
  };

  const loadFormatsMatrix = () => {
    if (typeof window === 'undefined' || !window.fetch) return;
    fetch('/api/formats', { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((data) => {
        if (data && Array.isArray(data.inputs) && Array.isArray(data.outputs) && data.capabilities && typeof data.capabilities === 'object') {
          formatsMatrix = data;
          renderFormats();
          renderFormatsChips();
          // DE-037: the archive preset is hidden when the
          // matrix reports avif: false. Call renderPresets
          // after the matrix loads so the gating takes
          // effect on first paint.
          renderPresets();
        } else {
          formatsFailed = true;
        }
      })
      .catch(() => {
        formatsFailed = true;
      });
  };

  const addFiles = (fileList) => {
    const seen = new Set();
    for (const it of items) seen.add(it.file.name + '|' + it.file.size + '|' + it.file.lastModified);
    let added = 0, skipped = 0, duplicates = 0, capped = false;
    for (const f of fileList) {
      if (!isImage(f)) { skipped++; continue; }
      const key = f.name + '|' + f.size + '|' + f.lastModified;
      if (seen.has(key)) { duplicates++; continue; }
      if (items.length >= MAX_QUEUE) { capped = true; continue; }
      seen.add(key);
      items.push({
        id: id(),
        file: f,
        blob: null,
        serverBlob: null,
        serverUrl: null,
        status: 'pending',
        beforeUrl: URL.createObjectURL(f),
        afterUrl: null,
      });
      added++;
    }
    if (added === 0) {
      const parts = [];
      if (skipped > 0)   parts.push(t('toast_skipped',   { n: skipped }));
      if (duplicates > 0) parts.push(t('toast_duplicate', { n: duplicates }));
      if (parts.length) showToast(parts.join(' · '));
      if (capped) showToast(t('toast_cap_hit'));
      return;
    }
    if (duplicates > 0) showToast(t('toast_duplicate', { n: duplicates }));
    if (capped) showToast(t('toast_cap_hit'));
    if (added > 0) {
      // show staging strip inside dropzone-inner; hide empty-copy
      dropzoneCopy.classList.add('is-hidden');
      staging.classList.add('is-active');
      renderStaging(); renderResults(); renderConvertBtn();
    }
  };

  // ---- URL fetch ----
  const setUrlStatus = (text, kind) => {
    urlStatus.innerHTML = text || '';
    urlStatus.classList.remove('is-err', 'is-ok');
    if (kind === 'err') urlStatus.classList.add('is-err');
    if (kind === 'ok')  urlStatus.classList.add('is-ok');
  };
  const isImageUrl = (u) => { try { const p = new URL(u); return /^https?:$/.test(p.protocol); } catch { return false; } };
  const fetchUrl = async (raw) => {
    const url = raw.trim();
    if (!url) { setUrlStatus(t('toast_invalid_url'), 'err'); return; }
    if (!isImageUrl(url)) { setUrlStatus(t('toast_invalid_url'), 'err'); return; }
    setUrlStatus('<span class="spinner"></span> ' + t('convert_busy') + '…', null);
    urlGo.disabled = true;
    try {
      const res = await fetch(url, { mode: 'cors', redirect: 'follow' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      let type = res.headers.get('content-type') || '';
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      if (!type.startsWith('image/')) {
        if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) type = 'image/jpeg';
        else if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E) type = 'image/png';
        else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) type = 'image/gif';
        else if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46) type = 'image/webp';
        else if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79) type = 'image/avif';
        else throw new Error('not an image (server returned ' + (type || 'unknown') + ')');
      }
      const pathname = new URL(url).pathname.split('/').pop() || 'image';
      const safeName = pathname.replace(/[^a-zA-Z0-9._-]+/g, '_');
      const ext = ({'image/jpeg':'jpg','image/png':'png','image/webp':'webp','image/gif':'gif','image/avif':'avif','image/bmp':'bmp'})[type] || 'bin';
      const filename = /\.[a-z0-9]+$/i.test(safeName) ? safeName : (safeName + '.' + ext);
      const file = new File([buf], filename, { type });
      setUrlStatus('✓ ' + fmt(buf.byteLength) + ' · ' + ext, 'ok');
      addFiles([file]);
    } catch (e) {
      const msg = (e && e.message) || 'fetch failed';
      const hint = /failed to fetch|networkerror|cors/i.test(msg) ? ' — ' + t('toast_url_cors') : '';
      setUrlStatus(t('toast_url_err') + hint, 'err');
    } finally {
      urlGo.disabled = false;
    }
  };
  urlForm?.addEventListener('submit', (e) => { e.preventDefault(); fetchUrl(urlInput.value); });
  urlInput?.addEventListener('keydown', (e) => { if (e.key === 'Escape') { urlInput.value = ''; urlInput.blur(); } });

  // ---- staging strip ----
  const renderStagingCount = () => {
    const pending = items.filter((i) => i.status === 'pending' || i.status === 'busy').length;
    stagingCount.textContent = pending;
  };
  const renderStaging = () => {
    stagingList.innerHTML = '';
    for (const it of items) {
      const tile = document.createElement('div');
      tile.className = 'staging__tile is-' + it.status;
      tile.style.backgroundImage = `url(${it.beforeUrl})`;
      tile.dataset.id = it.id;
      tile.title = it.file.name;

      const cap = document.createElement('span');
      cap.className = 'staging__tile__caption';
      const nm = it.file.name;
      cap.textContent = nm.length > 18 ? nm.slice(0, 15) + '…' + nm.slice(-2) : nm;
      tile.appendChild(cap);

      const stat = document.createElement('span');
      stat.className = 'staging__tile__status';
      if (it.status === 'pending') stat.textContent = t('queued');
      else if (it.status === 'busy') stat.textContent = t('busy');
      else if (it.status === 'done') stat.textContent = t('done');
      else if (it.status === 'err') stat.textContent = t('failed');
      tile.appendChild(stat);

      const del = document.createElement('button');
      del.className = 'staging__tile__del';
      del.type = 'button';
      del.setAttribute('data-i18n-attr', 'aria-label');
      del.setAttribute('data-key', 'aria_remove');
      del.setAttribute('aria-label', t('aria_remove'));
      del.disabled = (it.status === 'busy');
      del.title = t('aria_remove');
      del.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        removeItem(it.id);
      });
      tile.appendChild(del);

      stagingList.appendChild(tile);
    }
    renderStagingCount();
    if (items.length === 0) {
      dropzoneCopy.classList.remove('is-hidden');
      staging.classList.remove('is-active');
    }
  };

  // collapseStaging: hide the staging strip inside the
  // dropzone and re-show the empty-state copy. Called
  // from runConvertQueue's finally block once the queue
  // is fully drained (every item in `done` or `err`
  // state) so the user sees the queue is finished and
  // a second "Convert" click is a deliberate re-run,
  // not an accidental re-encode. The items[] array is
  // NOT mutated, so the results strip and the batch
  // handler keep working unchanged.
  const collapseStaging = () => {
    dropzoneCopy.classList.remove('is-hidden');
    staging.classList.remove('is-active');
  };

  // remove one item: from anywhere (staging + results), revoke its URLs
  const removeItem = (itemId) => {
    const i = items.findIndex((x) => x.id === itemId);
    if (i < 0) return;
    const it = items[i];
    URL.revokeObjectURL(it.beforeUrl);
    if (it.afterUrl) URL.revokeObjectURL(it.afterUrl);
    if (it.serverUrl) URL.revokeObjectURL(it.serverUrl);
    items.splice(i, 1);
    if (openCompareId === itemId) {
      openCompareId = null;
      if (baCompareItem && baCompareItem.id === itemId) {
        compareModal.classList.remove('is-open');
        compareModal.hidden = true;
        compareModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        baCompareItem = null;
      }
    }
    renderStaging(); renderResults(); renderConvertBtn();
  };

  // clear button
  resultsClear?.addEventListener('click', () => {
    if (compareModal.classList.contains('is-open')) {
      compareModal.classList.remove('is-open');
      compareModal.hidden = true;
      compareModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      baCompareItem = null;
    }
    items.forEach((it) => { URL.revokeObjectURL(it.beforeUrl); if (it.afterUrl) URL.revokeObjectURL(it.afterUrl); if (it.serverUrl) URL.revokeObjectURL(it.serverUrl); });
    items.length = 0;
    openCompareId = null;
    renderStaging(); renderResults(); renderConvertBtn();
  });

  // ---- results strip ----
  const renderResultsCount = () => {
    const done = items.filter((i) => i.status === 'done').length;
    const total = items.length;
    resultsCount.textContent = done;
    resultsTotal.textContent = total;
    resultsDlAll.disabled = done === 0;
    if (total === 0) {
      results.classList.add('is-hidden');
    } else {
      results.classList.remove('is-hidden');
    }
    if (total === 0 || done === 0) {
      resultsEmpty.classList.remove('is-hidden');
    } else {
      resultsEmpty.classList.add('is-hidden');
    }
  };

  const renderResults = () => {
    resultsList.innerHTML = '';
    // if the modal holds an item that no longer exists (e.g. cleared or
    // removed via another path), close the modal first
    if (baCompareItem && !items.includes(baCompareItem)) {
      compareModal.classList.remove('is-open');
      compareModal.hidden = true;
      compareModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      baCompareItem = null;
      openCompareId = null;
    }
    for (const it of items) {
      if (it.status !== 'done') continue;
      const tile = document.createElement('div');
      tile.className = 'results-tile';
      tile.dataset.id = it.id;
      tile.setAttribute('role', 'button');
      tile.setAttribute('tabindex', '0');
      tile.setAttribute('data-i18n-attr', 'aria-label');
      tile.setAttribute('data-key', 'aria_compare');
      tile.setAttribute('aria-label', t('aria_compare'));

      const row = document.createElement('div');
      row.className = 'results-tile__row';

      const thumb = document.createElement('div');
      thumb.className = 'results-tile__thumb';
      // AC-DE-009-D1: thumb uses server-encoded blob (it.serverUrl), NEVER beforeUrl
      thumb.style.backgroundImage = `url(${it.serverUrl})`;
      row.appendChild(thumb);

      const body = document.createElement('div');
      body.className = 'results-tile__body';
      const name = document.createElement('div');
      name.className = 'results-tile__name';
      name.textContent = it.file.name;
      body.appendChild(name);
      const meta = document.createElement('div');
      meta.className = 'results-tile__meta';
      const ext = extFor(targetMime());
      const fb = finalBlob(it);
      if (fb) {
        const d = pct(it.file.size, fb.size);
        meta.textContent = `${ext} · ${fmt(fb.size)}`;
        body.appendChild(meta);
        const delta = document.createElement('span');
        delta.className = 'results-tile__delta ' + (d > 0 ? 'is-save' : (d < 0 ? 'is-grow' : ''));
        delta.textContent = (d > 0 ? '↓' : (d < 0 ? '↑' : '·')) + ' ' + Math.abs(d) + '%';
        row.appendChild(delta);
      }

      // CR-048: per-row action group (download + delete).
      // The buttons are siblings of body and delta inside
      // row (not children of body), so click events on the
      // buttons do not bubble up to tile's open-compare
      // listener; e.stopPropagation() in each handler is
      // belt-and-braces (matches the staging-strip pattern
      // at lines 1497-1500). downloadOne() and removeItem()
      // are existing helpers; removeItem() already closes
      // the compare-modal if it held the deleted item, and
      // the existing batch handler at 1846-1886 already
      // filters items[] on every click so the next
      // "Скачать всё (.zip)" automatically excludes the
      // deleted file.
      const actions = document.createElement('div');
      actions.className = 'results-tile__actions';

      const dl = document.createElement('button');
      dl.type = 'button';
      dl.className = 'results-tile__action';
      dl.setAttribute('data-i18n-attr', 'aria-label,title');
      dl.setAttribute('data-key', 'aria_dl_one');
      dl.setAttribute('aria-label', t('aria_dl_one'));
      dl.title = t('aria_dl_one');
      dl.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v12m0 0l-5-5m5 5l5-5M5 20h14"/></svg>';
      dl.addEventListener('click', (e) => {
        e.stopPropagation();
        downloadOne(it);
      });
      actions.appendChild(dl);

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'results-tile__action is-destructive';
      del.setAttribute('data-i18n-attr', 'aria-label,title');
      del.setAttribute('data-key', 'aria_remove_result');
      del.setAttribute('aria-label', t('aria_remove_result'));
      del.title = t('aria_remove_result');
      del.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 7h12m-9 0V4h6v3m-7 0v13a1 1 0 001 1h8a1 1 0 001-1V7"/></svg>';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        removeItem(it.id);
      });
      actions.appendChild(del);

      row.appendChild(actions);

      row.appendChild(body);
      tile.appendChild(row);

      // open compare-modal on click / Enter / Space
      tile.addEventListener('click', () => openCompare(it));
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openCompare(it);
        }
      });

      resultsList.appendChild(tile);
    }
    renderResultsCount();
  };

  // ---- compare modal (before/after slider) ----
  const setSliderPos = (pct) => {
    const p = Math.max(0, Math.min(100, pct));
    baBefore.style.clipPath = 'inset(0 ' + (100 - p) + '% 0 0)';
    baBefore.style.webkitClipPath = 'inset(0 ' + (100 - p) + '% 0 0)';
    baHandle.style.left = p + '%';
    baSlider.dataset.pos = String(p);
    baHandle.setAttribute('aria-valuenow', String(Math.round(p)));
  };

  // wait for an <img> to settle (loaded or error) so naturalWidth is readable
  const imgReady = (img) => new Promise((resolve) => {
    if (img.complete && img.naturalWidth) return resolve();
    img.addEventListener('load',  () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });

  const openCompare = async (it) => {
    baCompareItem = it;
    openCompareId = it.id;
    baBefore.removeAttribute('src');
    baAfter.removeAttribute('src');
    baBeforeSize.textContent = fmt(it.file.size);
    const fb = finalBlob(it);
    baAfterSize.textContent = fmt(fb ? fb.size : 0);
    const ext = extFor(targetMime());
    baAfterLabel.textContent = ext;
    const d = fb ? pct(it.file.size, fb.size) : 0;
    baDelta.className = 'ba-meta__delta ' + (d > 0 ? 'is-save' : (d < 0 ? 'is-grow' : ''));
    baDelta.textContent = d > 0 ? '\u2193' + d + '%' : (d < 0 ? '\u2191' + Math.abs(d) + '%' : '\u00b7');
    baName.textContent = it.file.name;
    baDownload.textContent = t('expander_download') + ' ' + ext.toUpperCase();
    setSliderPos(50);
    compareModal.hidden = false;
    compareModal.classList.add('is-open');
    compareModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    baBefore.src = it.beforeUrl;
    baAfter.src  = it.serverUrl;
    await Promise.all([imgReady(baBefore), imgReady(baAfter)]);

    // pick the wider aspect ratio so both images fit without vertical
    // letterboxing mismatch (which is what produced the 1-2 px
    // vertical drift the operator saw on the divider).
    const ratios = [baBefore, baAfter]
      .map((img) => img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0)
      .filter((r) => r > 0);
    if (ratios.length) {
      const widest = Math.max(...ratios);
      baSlider.style.aspectRatio = widest + ' / 1';
    }
  };

  const closeCompare = () => {
    if (!compareModal.classList.contains('is-open')) return;
    compareModal.classList.remove('is-open');
    compareModal.hidden = true;
    compareModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    baCompareItem = null;
    openCompareId = null;
    renderResults();
  };

  compareModal.querySelectorAll('[data-close]').forEach((el) => {
    el.addEventListener('click', closeCompare);
  });

  // Esc closes modal first; falls through to global Esc handler below
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && compareModal.classList.contains('is-open')) {
      closeCompare();
    }
  });

  // slider drag (mouse + touch via pointer events)
  let baDragging = false;
  const onPointerMove = (e) => {
    if (!baDragging) return;
    const rect = baSlider.getBoundingClientRect();
    setSliderPos(((e.clientX - rect.left) / rect.width) * 100);
  };
  const onPointerUp = () => {
    baDragging = false;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
  };
  baSlider.addEventListener('pointerdown', (e) => {
    baDragging = true;
    const rect = baSlider.getBoundingClientRect();
    setSliderPos(((e.clientX - rect.left) / rect.width) * 100);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    e.preventDefault();
  });

  // keyboard on handle
  baHandle.addEventListener('keydown', (e) => {
    let p = Number(baSlider.dataset.pos) || 50;
    if (e.key === 'ArrowLeft')       p -= e.shiftKey ? 10 : 2;
    else if (e.key === 'ArrowRight') p += e.shiftKey ? 10 : 2;
    else if (e.key === 'Home')       p = 0;
    else if (e.key === 'End')        p = 100;
    else return;
    setSliderPos(p);
    e.preventDefault();
  });

  baDownload.addEventListener('click', () => {
    if (baCompareItem) downloadOne(baCompareItem);
  });

  // ---- Convert button + worker pool ----
  const renderConvertBtn = () => {
    const pending = items.filter((i) => i.status === 'pending').length;
    const busy = items.filter((i) => i.status === 'busy').length;
    const k = busy, n = pending + busy;
    if (pending === 0 && busy === 0) {
      convertBtn.disabled = true;
      convertBtnLbl.setAttribute('data-i18n', 'convert_btn');
      convertBtnLbl.innerHTML = t('convert_btn');
    } else if (busy > 0) {
      convertBtn.disabled = true;
      convertBtnLbl.setAttribute('data-i18n', 'convert_progress');
      convertBtnLbl.innerHTML = t('convert_progress', { k, n });
    } else {
      convertBtn.disabled = false;
      convertBtnLbl.setAttribute('data-i18n', 'convert_btn');
      convertBtnLbl.innerHTML = t('convert_btn');
    }
  };

  const renderConvertProgress = () => {
    const pending = items.filter((i) => i.status === 'pending').length;
    const busy = items.filter((i) => i.status === 'busy').length;
    const done = items.filter((i) => i.status === 'done').length;
    const total = items.length;
    const inFlight = busy;
    if (inFlight > 0) {
      progressVisible = true;
      convertProg.hidden = false;
      convertProg.classList.add('is-active');
      convertProgFill.style.setProperty('--p', Math.round((done / total) * 100) + '%');
      const k = done + busy, n = total;
      convertProgText.innerHTML = t('convert_progress', { k, n });
    } else {
      progressVisible = false;
      convertProg.hidden = true;
      convertProg.classList.remove('is-active');
    }
  };

  const runConvertQueue = async () => {
    if (poolRunning) return;
    poolRunning = true;
    try {
      // Explicit Convert click: any previously-encoded blobs were
      // produced under the old quality/format/alpha settings. Drop
      // them so the worker pool re-encodes every pending item under
      // the current settings.
      items.forEach((it) => {
        if (it.status === 'done') {
          if (it.serverUrl) URL.revokeObjectURL(it.serverUrl);
          it.serverBlob = null;
          it.serverUrl = null;
          it.status = 'pending';
        }
      });
      renderStaging(); renderResults(); renderConvertBtn();
      const inFlight = new Set();
      while (true) {
        while (inFlight.size < BATCH) {
          const next = items.find((i) => i.status === 'pending' && !inFlight.has(i));
          if (!next) break;
          next.status = 'busy';
          const idx = items.indexOf(next);
          const p = convertOne(next, idx)
            .catch(() => { /* already mapped to err in convertOne */ })
            .finally(() => { inFlight.delete(p); });
          inFlight.add(p);
          renderStaging(); renderResults(); renderConvertBtn();
          renderConvertProgress();
        }
        if (inFlight.size === 0) break;
        await Promise.race([...inFlight]);
      }
    } finally {
      poolRunning = false;
      renderStaging(); renderResults(); renderConvertBtn();
      renderConvertProgress();
      if (items.every((it) => it.status === 'done' || it.status === 'err')) {
        collapseStaging();
      }
    }
  };

  convertBtn?.addEventListener('click', runConvertQueue);

  // CR-050: per-item upload to the server-side
  // session store. Best-effort: a network failure logs
  // a one-shot console.warn and returns. CR-051 will
  // re-upload on 404.
  //
  // CR-051: the body is the raw blob bytes, NOT a
  // multipart envelope — the server streams r.Body
  // verbatim, so a multipart FormData would store the
  // envelope bytes (boundary + trailing marker) as the
  // file content and corrupt the second-click zip.
  const uploadEncodedToSession = async (it) => {
    const blob = finalBlob(it);
    if (!blob) return;
    const sid = await ensureSessionId();
    if (!sid) return;
    const ext = extFor(targetMime());
    const name = outName(it, ext);
    try {
      await fetch('/api/sessions/' + sid + '/files/' + encodeURIComponent(name), {
        method: 'PUT',
        headers: { 'Content-Type': blob.type || 'application/octet-stream' },
        body: blob,
      });
    } catch (err) {
      console.warn('session upload failed', sid, name, err);
    }
  };

  const downloadOne = (it) => {
    const ext = extFor(targetMime());
    const blob = finalBlob(it);
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = outName(it, ext);
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  };

  resultsDlAll?.addEventListener('click', async () => {
    const done = items.filter((i) => finalBlob(i));
    if (!done.length) return;
    // CR-051: stream the archive from the per-session
    // on-disk store (CR-050) instead of re-uploading
    // every encoded blob to /api/convert-batch. The
    // second "Скачать всё" click no longer re-encodes;
    // the server reads the bytes from var/sessions/<id>/
    // and streams a fresh zip. The 404 path (session
    // swept by the TTL ticker or fresh page load without
    // a prior upload) re-uploads the local blobs to a
    // fresh session id and retries once.
    const sid = await ensureSessionId();
    if (!sid) {
      showToast(t('toast_network'));
      return;
    }
    const headers = { 'cache': 'no-store' };
    let resp;
    try { resp = await fetch('/api/sessions/' + sid + '/zip', { method: 'GET', cache: 'no-store' }); }
    catch (_) { showToast(t('toast_network')); return; }
    if (resp.status === 404) {
      // Session expired or never written (the user
      // opened a fresh tab without prior uploads).
      // Re-upload the local blobs to a fresh session
      // and retry once. uploadEncodedToSession awaits
      // ensureSessionId internally so the underlying
      // id may have been re-allocated by the server
      // after the 404.
      try {
        await Promise.all(done.map(uploadEncodedToSession));
        const sid2 = await ensureSessionId();
        if (!sid2) { showToast(t('toast_no_session')); return; }
        try { resp = await fetch('/api/sessions/' + sid2 + '/zip', { method: 'GET', cache: 'no-store' }); }
        catch (_) { showToast(t('toast_network')); return; }
        if (!resp.ok) { showToast(t('toast_no_session')); return; }
      } catch (_) {
        showToast(t('toast_no_session'));
        return;
      }
    } else if (!resp.ok) {
      let env = null;
      try { env = await resp.json(); } catch (_) { /* keep null */ }
      if (resp.status === 413) { showToast(t('toast_too_big')); return; }
      showToast((env && env.message) || ('session zip error (' + resp.status + ')'));
      return;
    }
    const blob = await resp.blob();
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : ('converter-batch-' + Date.now() + '.zip');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    showToast(filename);
  });

  // Server-side blob is the source of truth for the final encoded file.
  // The in-browser preview (`it.blob`/`it.afterUrl`) still feeds the page-side preview.
  const finalBlob = (it) => it.serverBlob || it.blob;

  // SHA-256 of a blob, first 32 hex chars (128 bits) — enough entropy
  // for unique filenames in a single batch and short enough to read.
  const sha256Short = async (blob) => {
    try {
      const buf = await blob.arrayBuffer();
      const hash = await crypto.subtle.digest('SHA-256', buf);
      const hex = Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, '0')).join('');
      return hex.slice(0, 32);
    } catch (_) {
      return id() + id(); // 16-char fallback when SubtleCrypto unavailable
    }
  };
  const outName = (it, ext) => (it.sha || shaFallback()) + '.' + ext;
  const shaFallback = () => id() + id();

  // ---- conversion pipeline ----
  const convertOne = async (it, idx) => {
    it.status = 'busy';
    if (idx >= 0 && items[idx]) items[idx] = it;

    let bitmap;
    try { bitmap = await createImageBitmap(it.file); }
    catch (e) {
      it.status = 'err';
      showToast(t('toast_decode', { name: it.file.name }));
      renderStaging(); renderResults(); renderConvertBtn();
      renderConvertProgress();
      return;
    }

    const canvas = document.createElement('canvas');
    let outW = bitmap.width, outH = bitmap.height;
    const rs = resizeSpec();
    if (rs) {
      if (rs.mode === 'auto') {
        // per-orientation cap: landscape (w >= h) -> cap width,
        // portrait (h > w) -> cap height. Each axis uses its own
        // user-supplied cap; aspect is preserved on whichever axis
        // is over budget.
        const isLandscape = outW >= outH;
        const cap = isLandscape ? rs.w : rs.h;
        const side = isLandscape ? outW : outH;
        if (side > cap) {
          const k = cap / side;
          if (isLandscape) {
            outW = cap;
            outH = Math.max(1, Math.round(outH * k));
          } else {
            outH = cap;
            outW = Math.max(1, Math.round(outW * k));
          }
        }
      } else {
        // single-axis cap:
        //   longest: longest side <= rs.max, aspect preserved
        //   width:   width        <= rs.max, height proportional
        //   height:  height       <= rs.max, width  proportional
        const { max, axis } = rs;
        const longest = Math.max(outW, outH);
        if (axis === 'longest' && longest > max) {
          const k = max / longest;
          outW = Math.max(1, Math.round(outW * k));
          outH = Math.max(1, Math.round(outH * k));
        } else if (axis === 'width' && outW > max) {
          const k = max / outW;
          outW = max;
          outH = Math.max(1, Math.round(outH * k));
        } else if (axis === 'height' && outH > max) {
          const k = max / outH;
          outH = max;
          outW = Math.max(1, Math.round(outW * k));
        }
      }
    }
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) { it.status = 'err'; renderStaging(); renderResults(); return; }
    const mime = targetMime();
    if (mime === 'image/jpeg' && !advAlpha.checked && hasAlpha(bitmap)) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(bitmap, 0, 0, outW, outH);
    bitmap.close?.();

    const q = Math.max(0.01, Math.min(1, Number(quality.value) / 100));
    const blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), mime, useQ(mime) ? q : undefined));
    if (!blob) { it.status = 'err'; renderStaging(); renderResults(); return; }
    it.blob = blob;
    it.afterUrl = URL.createObjectURL(blob);

    // Same-mime re-encode (jpg -> jpg, png -> png, webp -> webp) is
    // a pure quality-driven recompression. canvas.toBlob already
    // produced a valid in-browser encode at the chosen quality, so
    // promote it to the canonical server slot. The convert-to-webp
    // CLI rejects same-format runs as a no-op (it never produces
    // an output file in that case), so going to /api/convert for
    // jpg -> jpg would surface as a silent failure — the operator
    // flagged exactly this in DE-017.
    //
    // Cross-format runs (jpg -> webp, png -> jpg, etc.) also use
    // the in-browser resized canvas blob so the user-chosen resize
    // cap (longest / width / height) is honoured uniformly. The
    // server-side CLI's --resize does not support height-only, so
    // we keep the resize path entirely on the client.
    if (mime === it.file.type) {
      it.serverBlob = it.blob;
      it.serverUrl = it.afterUrl;
      it.sha = await sha256Short(it.serverBlob);
    } else {
      try {
        const serverBlob = await convertOnServer(it.blob, mime, it.file.type);
        it.serverBlob = serverBlob;
        it.serverUrl = URL.createObjectURL(serverBlob);
        it.sha = await sha256Short(serverBlob);
      } catch (err) {
        if (err && err.__toast) showToast(err.message);
        it.status = 'err';
        renderStaging(); renderResults(); renderConvertBtn();
        renderConvertProgress();
        return;
      }
    }
    it.status = 'done';
    // CR-050: best-effort upload of the encoded file
    // to the server-side session store. A failure
    // (network error, 413, expired session) is logged
    // once via console.warn and the local items[] entry
    // is preserved — the next "Скачать всё" click
    // (CR-051 rewrite) re-uploads if the session is
    // missing. sendBeacon is not used here because the
    // payload is a multipart Blob, not a small JSON
    // body; sendBeacon is reserved for the page-unload
    // DELETE call (CR-052).
    uploadEncodedToSession(it);
    renderStaging(); renderResults(); renderConvertBtn();
    renderConvertProgress();
  };

  const shortFmt = (mime) => (
    mime === 'image/jpeg' ? 'jpeg' :
    mime === 'image/png'  ? 'png'  :
    mime === 'image/webp' ? 'webp' : ''
  );
  const toastErr = (msg) => { const e = new Error(msg); e.__toast = true; return e; };
  // CR-092: per-file upload cap. The constant matches the backend's
  // maxRequestBytes (backend/cmd/converter-server/main.go:23) so a
  // rejected client-side upload never reaches the server.
  const MAX_FILE_BYTES = 10 * 1024 * 1024;

  const convertOnServer = async (body, targetMimeValue, srcMime) => {
    if (body.size > MAX_FILE_BYTES) throw toastErr(t('toast_too_big'));
    const headers = {
      'X-Output-Format': shortFmt(targetMimeValue) || 'webp',
      'X-Quality': String(Number(quality.value) || 82),
    };
    // DE-030: X-Encoder-Options is the per-codec fine-tune
    // header set built by the page-side "advanced" panel.
    // The accessor returns an empty string when the panel
    // is collapsed and no input has been touched, in which
    // case the header is omitted (the byte-identical
    // pre-DE-030 baseline is preserved; AC-DE-030-4).
    const encOpts = getEncoderOptions();
    if (encOpts) headers['X-Encoder-Options'] = encOpts;
    const inFmt = shortFmt(srcMime);
    if (inFmt) headers['X-Input-Format'] = inFmt;
    // resize is applied client-side in convertOne() via canvas drawImage
    // before this round-trip; the convert-to-webp CLI's --resize does
    // not support height-only, so we keep that path on the client to
    // honour the 'height' axis uniformly. The body here is the
    // already-resized canvas blob, not the original File.
    let resp;
    try { resp = await fetch('/api/convert', { method: 'POST', headers, body }); }
    catch (_) { throw toastErr(t('toast_network')); }
    if (resp.ok) return await resp.blob();
    // Non-2xx path: always parse the JSON envelope first
    // (AC-DE-030-5: the page must surface the verbatim
    // error.message for any 400, including encoder-options
    // validation errors). The previous "400 -> toast_too_big"
    // short-circuit was wrong because encoder-options
    // errors are 400, not 413.
    let env = null;
    try { env = await resp.json(); } catch (_) { /* keep null */ }
    // 413 (Content Too Large) is the only envelope-less
    // case the backend surfaces today; the body cap check
    // rejects with 400 bad_request "body too large" too,
    // so we fall through to the envelope path either way.
    if (!env) {
      if (resp.status === 413) throw toastErr(t('toast_too_big'));
      throw toastErr('server error');
    }
    const msg = env.message || 'converter error';
    const prefix = resp.status === 422 ? `${t('toast_decode', { name: body.name || 'image' })} — ${msg}` : `server error — ${msg}`;
    throw toastErr(prefix);
  };
  const hasAlpha = (bitmap) => {
    if (targetMime() === 'image/jpeg') return false;
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    const cx = c.getContext('2d');
    cx.drawImage(bitmap, 0, 0, 1, 1);
    try { return cx.getImageData(0, 0, 1, 1).data[3] < 255; } catch { return false; }
  };

  // ---- drop zones ----
  const wireDrop = (zone) => {
    if (!zone) return;
    zone.addEventListener('click', (e) => {
      // do not intercept clicks inside the staging strip
      if (e.target.closest('.staging')) return;
      fileInput.click();
    });
    zone.addEventListener('keydown', (e) => {
      // Enter/Space only opens browse when in empty state
      if (items.length === 0 && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault(); fileInput.click();
      }
    });
    ['dragenter','dragover'].forEach((ev) => zone.addEventListener(ev, (e) => {
      e.preventDefault(); zone.classList.add('is-drag');
    }));
    ['dragleave','dragend','drop'].forEach((ev) => zone.addEventListener(ev, (e) => {
      e.preventDefault(); zone.classList.remove('is-drag');
    }));
    zone.addEventListener('drop', (e) => {
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    });
  };
  wireDrop(dz);
  wireDrop(addmoreBtn);
  window.addEventListener('paste', (e) => {
    const files = [...(e.clipboardData?.files || [])];
    if (files.length) addFiles(files);
  });
  fileInput?.addEventListener('change', (e) => {
    addFiles(e.target.files);
    fileInput.value = '';
  });

  // ---- lang switcher ----
  // The pill is now real anchor links (SA-001 / DE-065): the EN
  // anchor points at http://127.0.0.1:8096/ and the RU anchor at
  // http://127.0.0.1:8095/ (absolute per-port URLs, each carrying
  // rel="alternate nofollow" so PageRank does not bleed across the
  // two sites). No JS click handler — the browser navigates to the
  // canonical locale URL. aria-current="true" on the current page
  // is set in the HTML.

  // ---- share (CR-046 / CR-110 / CR-113) ----
  // Capability-gated share button. The Web Share API exists on
  // every modern browser but is gated by user-activation,
  // secure-context, and a per-platform allowlist (desktop Chrome
  // outside ChromeOS does not implement it). The capability check
  // combines all three signals and runs a `canShare` pre-flight so
  // a guaranteed-not-to-work round-trip is avoided.
  //
  // The clipboard.writeText fallback requires the same secure
  // context + user-activation as the share API; when neither
  // path is available, the inline indicator adjacent to the
  // button fires `share_failed` so the operator sees the click
  // outcome next to the click point (the page-bottom toast is
  // too far to register for a header button click).
  const shareBtn = document.getElementById('share-btn');
  const shareIndicator = document.getElementById('share-indicator');
  const SHARE_INDICATOR_MS = 1500;
  let shareIndicatorTimer = null;

  // CR-113: surface the click outcome adjacent to the button.
  // AbortError (user dismissed the native sheet) is silent; the
  // success / failure paths toggle the inline indicator for ~1.5s.
  const showShareIndicator = (kind, message) => {
    if (!shareIndicator) return;
    shareIndicator.textContent = message;
    shareIndicator.classList.remove('is-copied', 'is-error');
    if (kind) shareIndicator.classList.add(kind);
    shareIndicator.hidden = false;
    if (shareIndicatorTimer) clearTimeout(shareIndicatorTimer);
    shareIndicatorTimer = setTimeout(() => {
      shareIndicator.hidden = true;
      shareIndicator.classList.remove('is-copied', 'is-error');
      shareIndicatorTimer = null;
    }, SHARE_INDICATOR_MS);
  };

  // CR-113: combined capability check. Property existence alone
  // is not equivalent to API usability — every modern browser
  // exposes `navigator.share` even when the API is gated. The
  // `canShare` pre-flight call returns false when the browser
  // knows it cannot fulfil the request (e.g. desktop Chrome on
  // non-ChromeOS platforms), avoiding a wasted round-trip +
  // non-AbortError rejection.
  const canUseWebShare = () => {
    if (typeof window === 'undefined') return false;
    if (typeof navigator === 'undefined') return false;
    if (typeof navigator.share !== 'function') return false;
    if (typeof navigator.canShare !== 'function') return false;
    if (!window.isSecureContext) return false;
    return true;
  };

  shareBtn?.addEventListener('click', async () => {
    const url = location.href;
    const title = document.title;
    // CR-110: derive the brand from the page title (the "— <brand>"
    // suffix the sitegen-rendered title carries). Falls back to the
    // current locale's apex-default brand when the title uses no
    // suffix (defensive; matches the historical apex-default deploy).
    const brandSegment = title.split(' — ').slice(-1)[0].trim();
    const localeBrand = ({
      en: 'elrise.io',
      ru: 'elrise.ru',
    })[currentLocale] || 'elrise.io';
    const text = `img convert — ${brandSegment || localeBrand}`;
    const sharePayload = { title, text, url };

    // CR-113: deterministic path. Pick the right branch the
    // first time via the capability check + canShare pre-flight,
    // not via try/catch fallthrough. The try/catch stays for
    // AbortError (silent) and runtime errors (console.warn).
    if (canUseWebShare() && navigator.canShare(sharePayload)) {
      try {
        await navigator.share(sharePayload);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // CR-113: log the non-AbortError rejection so DevTools
        // shows a breadcrumb. The text-level comment at the
        // catch site is the canonical "why" — the actual fix
        // is the capability gate above; this catch is the
        // last-resort path for transient runtime errors.
        console.warn('[share] navigator.share rejected:', err);
        showShareIndicator('is-error', FORM_I18N[currentLocale].share_failed);
        return;
      }
    }

    // Fallback path: clipboard.writeText requires the same
    // secure context + user-activation as the share API. The
    // click provides user-activation; secure-context is the
    // only thing that can fail here (HTTP non-loopback hostname
    // behind a proxy that strips the secure-context signal).
    if (window.isSecureContext && typeof navigator.clipboard?.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(url);
        showShareIndicator('is-copied', FORM_I18N[currentLocale].share_copied);
        return;
      } catch (err) {
        console.warn('[share] clipboard.writeText rejected:', err);
        showShareIndicator('is-error', FORM_I18N[currentLocale].share_failed);
        return;
      }
    }

    // Last-resort path: neither API is available. Surface the
    // failure inline so the operator sees the click outcome
    // adjacent to the button. The page-bottom toast is
    // intentionally NOT used here (the operator's reproducer
    // reported "nothing happens" because the toast was too
    // far from the click point).
    showShareIndicator('is-error', FORM_I18N[currentLocale].share_failed);
  });

  // ---- global keys ----
  window.addEventListener('keydown', (e) => {
    // Esc clears stage and revokes all Blob URLs (DE-006 invariant E6)
    if (e.key === 'Escape' && items.length) {
      items.forEach((it) => { URL.revokeObjectURL(it.beforeUrl); if (it.afterUrl) URL.revokeObjectURL(it.afterUrl); if (it.serverUrl) URL.revokeObjectURL(it.serverUrl); });
      items.length = 0;
      openCompareId = null;
      renderStaging(); renderResults(); renderConvertBtn();
      renderConvertProgress();
      setUrlStatus('', null);
    }
    // Enter on empty dropzone triggers browse (handled by dropzone keydown);
    // Enter inside an active compare expander does nothing extra here.
  });

  // CR-052: cleanup on page unload + long-idle visibility.
  // The session-store folder under var/sessions/<id>/ is
  // the source of truth for the encoded bytes; once the
  // user closes the tab, the folder is no longer useful
  // and is removed by DELETE /api/sessions/<id>.
  // sendBeacon is the canonical page-unload transport
  // (the request is queued by the browser and survives
  // the page being torn down). The fetch-with-keepalive
  // fallback covers browsers without sendBeacon.
  const cleanupSession = () => {
    if (!currentSessionId) return;
    const url = '/api/sessions/' + currentSessionId;
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, '');
        return;
      }
    } catch (_) { /* fall through to fetch */ }
    try {
      fetch(url, { method: 'DELETE', keepalive: true }).catch(() => {});
    } catch (_) { /* best-effort */ }
  };
  window.addEventListener('pagehide', cleanupSession);
  // visibilitychange fires on every transition; the
  // lastActiveTs guard suppresses a spurious cleanup on
  // a short tab-switch (the 3-minute TTL + 2-minute grace
  // covers the "user walked away for > 5 minutes" case).
  let lastActiveTs = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      lastActiveTs = Date.now();
      return;
    }
    if (Date.now() - lastActiveTs > 5 * 60 * 1000) {
      cleanupSession();
    }
  });

const statsDashEl    = document.getElementById('stats-dash');
  const statsNoticeEl = document.getElementById('stats-notice');
  let statsData = null;
  let statsFailed = false;
  // CR-053: track the failure kind so renderStatsFromCache
  // can pick the right per-cause i18n key. Values: null,
  // 'validation' (HTTP 400), 'backend' (HTTP 503/5xx),
  // 'network' (fetch rejected — reverse-proxy not
  // routing, server down, CORS).
  let statsFailureKind = null;
  // CR-056: cache expanded-view data per range key (24h/7d/30d/90d).
  // The initial fetch populates the 30d entry; tab clicks hit the
  // cache and only refetch on miss.
  const statsDashCache = new Map();
  let statsActiveRange = '30d';
  let statsDashFetchInFlight = null;

  const statsFormatters = () => ({
    int:     new Intl.NumberFormat(locale),
    compact: new Intl.NumberFormat(locale, { notation: 'compact', maximumSignificantDigits: 2 }),
    pct:     new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
  });

  const renderStatsFromCache = () => {
    if (statsFailed) {
      if (statsChartEl) statsChartEl.hidden = true;
      if (statsNoticeEl) {
        const key = statsFailureKind === 'validation'
          ? 'stats_error_validation'
          : statsFailureKind === 'backend'
            ? 'stats_error_backend'
            : statsFailureKind === 'network'
              ? 'stats_error_network'
              : 'stats_error';
        statsNoticeEl.setAttribute('data-i18n', key);
        statsNoticeEl.textContent = t(key);
        statsNoticeEl.classList.remove('is-hidden');
      }
      if (statsDashEl) {
        statsDashEl.hidden = true;
        clearStatsDash();
      }
      return;
    }
    if (!statsData || !statsData.totals) return;
    const totals = statsData.totals;
    const fmt = statsFormatters();
    const files = Number(totals.files);
    const inputBytes = Number(totals.inputBytes);
    const outputBytes = Number(totals.outputBytes);
    const avgCompression = totals.avgCompression == null ? null : Number(totals.avgCompression);
    if (!Number.isFinite(files) || !Number.isFinite(inputBytes) || !Number.isFinite(outputBytes) || (avgCompression != null && !Number.isFinite(avgCompression))) {
      statsFailed = true;
      renderStatsFromCache();
      return;
    }
    if (statsNoticeEl) statsNoticeEl.classList.add('is-hidden');
    renderStatsSparkline(statsData.buckets, fmt);
  };

  const statsChartEl = document.getElementById('stats-chart');
  const statsSparklineEl = document.getElementById('stats-sparkline');
  const statsChartPeakEl = document.getElementById('stats-chart-peak');

  // Build a small 30-day sparkline from the buckets array. Renders
  // bars under a smooth line, marks the peak day with a glowing dot,
  // and shows the peak count in the header. Respects prefers-reduced-
  // motion (CSS handles the animation gate) and is a no-op when the
  // bucket array is empty or carries no signal.
  const renderStatsSparkline = (buckets, fmt) => {
    if (!statsChartEl || !statsSparklineEl) return;
    const series = Array.isArray(buckets) ? buckets
      .map((b) => ({ ts: Number(b && b.tsMs), files: Number(b && b.files) }))
      .filter((b) => Number.isFinite(b.ts) && Number.isFinite(b.files))
      : [];
    if (series.length < 2) {
      statsChartEl.hidden = true;
      return;
    }
    const W = 300;
    const H = 60;
    const padX = 2;
    const padY = 6;
    const innerW = W - padX * 2;
    const innerH = H - padY * 2;
    const max = series.reduce((m, b) => Math.max(m, b.files), 0);
    const peakIdx = series.reduce((bestIdx, b, i) => b.files > series[bestIdx].files ? i : bestIdx, 0);
    const stepX = innerW / (series.length - 1);
    const points = series.map((b, i) => {
      const x = padX + i * stepX;
      const y = padY + (max > 0 ? innerH - (b.files / max) * innerH : innerH);
      return [x, y];
    });
    const linePath = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(2) + ',' + p[1].toFixed(2)).join(' ');
    const areaPath = linePath
      + ' L' + points[points.length - 1][0].toFixed(2) + ',' + (H - padY).toFixed(2)
      + ' L' + points[0][0].toFixed(2) + ',' + (H - padY).toFixed(2) + ' Z';
    // Smooth cubic bezier through the points (Catmull-Rom -> Bezier).
    const smoothPath = catmullRomToBezier(points);
    const smoothArea = smoothPath
      + ' L' + points[points.length - 1][0].toFixed(2) + ',' + (H - padY).toFixed(2)
      + ' L' + points[0][0].toFixed(2) + ',' + (H - padY).toFixed(2) + ' Z';
    const peakX = points[peakIdx][0];
    const peakY = points[peakIdx][1];

    let bars = '';
    const barW = Math.max(1.2, stepX * 0.55);
    for (let i = 0; i < series.length; i++) {
      const cx = points[i][0];
      const baseY = H - padY;
      const h = max > 0 ? (series[i].files / max) * innerH : 0;
      bars += '<rect class="spark-bar" x="' + (cx - barW / 2).toFixed(2) + '" y="' + (baseY - h).toFixed(2) + '" width="' + barW.toFixed(2) + '" height="' + Math.max(0.5, h).toFixed(2) + '"></rect>';
    }

    statsSparklineEl.innerHTML =
      '<defs>'
        + '<linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">'
        + '<stop offset="0%" stop-color="rgba(78,201,154,0.75)"/>'
        + '<stop offset="55%" stop-color="rgba(78,201,154,0.30)"/>'
        + '<stop offset="100%" stop-color="rgba(78,201,154,0)"/>'
        + '</linearGradient>'
        + '</defs>'
      + bars
      + '<path class="spark-area" d="' + smoothArea + '"></path>'
      + '<path class="spark-line" d="' + smoothPath + '"></path>'
      + '<line class="spark-today" x1="' + points[points.length - 1][0].toFixed(2) + '" y1="' + padY + '" x2="' + points[points.length - 1][0].toFixed(2) + '" y2="' + (H - padY) + '"></line>'
      + '<circle class="spark-peak-ring" cx="' + peakX.toFixed(2) + '" cy="' + peakY.toFixed(2) + '" r="4"></circle>'
      + '<circle class="spark-peak" cx="' + peakX.toFixed(2) + '" cy="' + peakY.toFixed(2) + '" r="3"></circle>';

    if (statsChartPeakEl) {
      const peakDate = new Date(series[peakIdx].ts);
      const dateLabel = peakDate.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      statsChartPeakEl.innerHTML = t('stats_chart_peak', { n: fmt.int.format(series[peakIdx].files) })
        + ' <span class="stats-band__chart-peak-date">· ' + dateLabel + '</span>';
    }
    statsChartEl.hidden = false;
  };

  // Catmull-Rom spline (tension 0.5) -> cubic Bezier control points.
  // Produces a smooth curve that passes through every data point;
  // matches the look of design tools' default smooth interpolation.
  const catmullRomToBezier = (pts) => {
    if (pts.length < 2) return '';
    if (pts.length === 2) return 'M' + pts[0][0] + ',' + pts[0][1] + ' L' + pts[1][0] + ',' + pts[1][1];
    const tension = 0.5;
    let d = 'M' + pts[0][0].toFixed(2) + ',' + pts[0][1].toFixed(2);
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension / 3;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension / 3;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension / 3;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension / 3;
      d += ' C' + cp1x.toFixed(2) + ',' + cp1y.toFixed(2) + ' ' + cp2x.toFixed(2) + ',' + cp2y.toFixed(2) + ' ' + p2[0].toFixed(2) + ',' + p2[1].toFixed(2);
    }
    return d;
  };

  // === CR-057: collapsed/expanded view toggle ===

  // localStorage key for the user's last-chosen stats view.
  // Mirrors the LS_KEY pattern used by the locale switcher
  // (LS_KEY = 'locale') so the two preferences live in the
  // same persistence layer.
  const STATS_VIEW_KEY = 'stats_view';

  const statsToggleEl = document.getElementById('stats-toggle');
  const statsCloseEl  = document.getElementById('stats-close');

  let statsView = 'collapsed';
  try {
    const stored = localStorage.getItem(STATS_VIEW_KEY);
    if (stored === 'expanded' || stored === 'collapsed') statsView = stored;
  } catch (_) { /* localStorage unavailable; default stays collapsed */ }

  // Apply the persisted view to the DOM on init. Called from
  // the dashboard wire-up block below so the dashboard's
  // cached data is available before the first render.
  const applyStatsView = (next) => {
    if (!statsDashEl) return;
    const expanded = next === 'expanded';
    if (statsToggleEl) statsToggleEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (statsDashEl) {
      statsDashEl.hidden = !expanded;
      if (expanded) {
        // Trigger the reduced-motion-safe fade-in (CSS only
        // applies the transition under `no-preference`).
        statsDashEl.classList.remove('is-fading');
        // Render the dashboard if cached data is available,
        // otherwise fetch on demand.
        const data = statsDashCache.get(statsActiveRange);
        if (data) renderStatsDash(data);
        else loadDashRange(statsActiveRange);
      } else if (!expanded) {
        statsDashEl.classList.add('is-fading');
      }
    }
    statsView = expanded ? 'expanded' : 'collapsed';
  };

  const persistStatsView = () => {
    try { localStorage.setItem(STATS_VIEW_KEY, statsView); } catch (_) { /* ignore */ }
  };

  const toggleStatsView = () => {
    applyStatsView(statsView === 'expanded' ? 'collapsed' : 'expanded');
    persistStatsView();
  };

  // Wire click + keyboard handlers. The toggle is a real
  // <button> so Enter / Space come for free; we still bind
  // click explicitly because some test harnesses fire
  // synthetic clicks rather than keyboard events.
  if (statsToggleEl) {
    statsToggleEl.addEventListener('click', toggleStatsView);
  }
  if (statsCloseEl) {
    statsCloseEl.addEventListener('click', () => {
      applyStatsView('collapsed');
      persistStatsView();
      // Return focus to the toggle so keyboard users do not
      // lose their place in the tab order.
      if (statsToggleEl) statsToggleEl.focus();
    });
  }

  // === CR-056: expanded stats dashboard (#stats-dash) ===

  // Map a range key to (sinceMs offset, bucket). The 31-day
  // hard cap from stats-api.md § 2.1 constrains the choices to
  // ranges that fit under it; CR-061 dropped the 90д tab because
  // the backend rejects any window > 31 days with a 400.
  const RANGE_CONFIG = {
    '24h': { hours: 24,            bucket: 'hour' },
    '7d':  { hours: 7  * 24,       bucket: 'hour' },
    '30d': { hours: 30 * 24,       bucket: 'day'  },
  };

  const fetchStatsRange = (range) => {
    const cfg = RANGE_CONFIG[range];
    if (!cfg) return Promise.reject(new Error('bad range: ' + range));
    const untilMs = Date.now();
    const sinceMs = untilMs - cfg.hours * 3600000;
    const url = '/api/stats?since=' + sinceMs + '&until=' + untilMs
      + '&bucket=' + cfg.bucket + '&status=all';
    return fetch(url, { credentials: 'omit' }).then((res) => {
      if (res.ok) return res.json().then((data) => ({ range, data }));
      const kind = res.status === 400 ? 'validation' : 'backend';
      throw new Error('HTTP ' + res.status + ':' + kind);
    });
  };

  // CR-060: per-pair aggregate for the dashboard's dual-bar list.
  // The same (since, until) shape as fetchStatsRange, but reads
  // from /api/stats/formats (CR-059) and the response carries
  // `pairs: [{input, output, total, success}]` already sorted by
  // total DESC. The cache key is the range key (the dashboard never
  // varies the limit).
  const formatsCache = new Map();
  const fetchFormatsRange = (range) => {
    const cfg = RANGE_CONFIG[range];
    if (!cfg) return Promise.reject(new Error('bad range: ' + range));
    const untilMs = Date.now();
    const sinceMs = untilMs - cfg.hours * 3600000;
    const url = '/api/stats/formats?since=' + sinceMs + '&until=' + untilMs + '&limit=12';
    return fetch(url, { credentials: 'omit' }).then((res) => {
      if (res.ok) return res.json().then((data) => ({ range, data }));
      const kind = res.status === 400 ? 'validation' : 'backend';
      throw new Error('HTTP ' + res.status + ':' + kind);
    });
  };

  const renderKpiSpark = (svgEl, series) => {
    if (!svgEl) return;
    const pts = Array.isArray(series) ? series
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v))
      : [];
    if (pts.length < 2) { svgEl.innerHTML = ''; return; }
    const W = 60, H = 18, padX = 2, padY = 3;
    const innerW = W - padX * 2;
    const innerH = H - padY * 2;
    const max = pts.reduce((m, v) => Math.max(m, v), 0);
    const min = pts.reduce((m, v) => Math.min(m, v), Infinity);
    const span = max - min;
    const stepX = innerW / (pts.length - 1);
    const points = pts.map((v, i) => {
      const x = padX + i * stepX;
      const norm = span > 0 ? (v - min) / span : 0.5;
      const y = padY + (1 - norm) * innerH;
      return [x, y];
    });
    const linePath = catmullRomToBezier(points);
    const areaPath = linePath
      + ' L' + points[points.length - 1][0].toFixed(2) + ',' + (H - padY).toFixed(2)
      + ' L' + points[0][0].toFixed(2) + ',' + (H - padY).toFixed(2) + ' Z';
    svgEl.innerHTML =
      '<path class="dash-spark-area" d="' + areaPath + '"></path>'
      + '<path class="dash-spark-line" d="' + linePath + '"></path>';
  };

  const renderActivityCanvas = (canvas, buckets) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const series = Array.isArray(buckets) ? buckets
      .map((b) => ({
        ts:    Number(b && b.tsMs),
        files: Number(b && b.files),
        errs:  Number(b && b.errCount),
      }))
      .filter((b) => Number.isFinite(b.ts) && Number.isFinite(b.files))
      : [];
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth || 600;
    const cssH = Number(canvas.getAttribute('height')) || 64;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    if (series.length < 1) return;
    const padX = 2, padY = 4;
    const innerW = cssW - padX * 2;
    const innerH = cssH - padY * 2;
    const totals = series.map((b) => Math.max(0, b.files) + Math.max(0, b.errs));
    const max = Math.max(1, ...totals);
    const barW = innerW / series.length;
    for (let i = 0; i < series.length; i++) {
      const b = series[i];
      const okCount = Math.max(0, b.files - b.errs);
      const errCount = Math.max(0, b.errs);
      const okH = (okCount / max) * innerH;
      const errH = (errCount / max) * innerH;
      const x = padX + i * barW + barW * 0.15;
      const w = Math.max(1, barW * 0.7);
      const baseY = padY + innerH;
      ctx.fillStyle = '#4ec99a';
      ctx.fillRect(x, baseY - okH, w, okH);
      ctx.fillStyle = '#ff6f6f';
      ctx.fillRect(x, baseY - okH - errH, w, errH);
    }
  };

  const formatCodeToLabel = (code) => {
    if (!code || typeof code !== 'string') return '';
    return code.toUpperCase();
  };

  // CR-060: render the per-pair aggregate as two stacked bars
  // (red = total, green overlay = success). Widths are normalised
  // to the largest total across the visible pairs so the bars
  // remain visually comparable.
  const renderFormatsDualBars = (host, pairs) => {
    if (!host) return;
    const list = Array.isArray(pairs) ? pairs : [];
    if (list.length === 0) {
      host.innerHTML = '<div class="stats-dash__list-empty" data-i18n="stats_dash_activity_legend_ok">'
        + t('stats_dash_activity_legend_ok') + '</div>';
      return;
    }
    const maxTotal = list.reduce((m, p) => Math.max(m, Number(p && p.total) || 0), 0) || 1;
    const totalLabel = t('stats_dash_format_total_label');
    const successLabel = t('stats_dash_format_success_label');
    host.innerHTML = list.map((p) => {
      const input = formatCodeToLabel(p && p.input);
      const output = formatCodeToLabel(p && p.output);
      const total = Number(p && p.total) || 0;
      const success = Number(p && p.success) || 0;
      const totalRatio = Math.max(0, Math.min(1, total / maxTotal));
      const successRatio = total > 0 ? Math.max(0, Math.min(1, success / total)) : 0;
      const aria = input + ' \u2192 ' + output + ': '
        + total + ' ' + totalLabel + ', '
        + success + ' ' + successLabel;
      return '<div class="stats-dash__format-row" role="listitem" aria-label="' + aria + '">'
        + '<span class="stats-dash__format-row-label">' + input + ' \u2192 ' + output + '</span>'
        + '<span class="stats-dash__format-row-counts">'
          + '<span>' + total + '</span>'
          + '<span>' + success + '</span>'
        + '</span>'
        + '<span class="stats-dash__format-row-bars">'
          + '<span class="stats-dash__format-row-bar--total" style="transform: scaleX(' + totalRatio.toFixed(3) + ')"></span>'
          + '<span class="stats-dash__format-row-bar--success" style="transform: scaleX(' + (totalRatio * successRatio).toFixed(3) + ')"></span>'
        + '</span>'
      + '</div>';
    }).join('');
  };

  const renderStatsDash = (data) => {
    if (!statsDashEl) return;
    const totals = (data && data.totals) || {};
    const fmt = statsFormatters();
    const files = Number(totals.files);
    const inputBytes = Number(totals.inputBytes);
    const outputBytes = Number(totals.outputBytes);
    const okCount = Number(totals.okCount);
    const avgMs = totals.avgDurationMs != null ? Number(totals.avgDurationMs)
      : (totals.p50DurationMs != null ? Number(totals.p50DurationMs) : null);

    const safeFiles = Number.isFinite(files) ? files : 0;
    const savedBytes = (Number.isFinite(inputBytes) && Number.isFinite(outputBytes))
      ? Math.max(0, inputBytes - outputBytes) : 0;
    const safeOk = Number.isFinite(okCount) ? okCount : 0;

    const setKpi = (key, value, unit) => {
      const card = statsDashEl.querySelector('[data-kpi="' + key + '"]');
      if (!card) return;
      const valEl = card.querySelector('[data-kpi-value]');
      const unitEl = card.querySelector('[data-kpi-unit]');
      if (valEl) valEl.textContent = value;
      if (unitEl) unitEl.textContent = unit;
    };

    setKpi('files', fmt.int.format(safeFiles), '');
    setKpi('ok', fmt.int.format(safeOk), safeFiles > 0
      ? fmt.pct.format(Math.round((safeOk / safeFiles) * 100)) + ' %'
      : '—');
    setKpi('saved', fmt.compact.format(savedBytes) + ' B', savedBytes > 0 && inputBytes > 0
      ? fmt.pct.format(Math.round((savedBytes / inputBytes) * 100)) + ' %'
      : '—');
    setKpi('avg', avgMs != null && Number.isFinite(avgMs)
      ? fmt.int.format(Math.round(avgMs))
      : '—', 'ms');

    const buckets = Array.isArray(data && data.buckets) ? data.buckets : [];
    renderKpiSpark(statsDashEl.querySelector('[data-kpi="files"] [data-kpi-spark]'),
      buckets.map((b) => Number(b && b.files)));
    renderKpiSpark(statsDashEl.querySelector('[data-kpi="ok"] [data-kpi-spark]'),
      buckets.map((b) => Math.max(0, Number(b && b.files) - Number(b && b.errCount))));
    renderKpiSpark(statsDashEl.querySelector('[data-kpi="saved"] [data-kpi-spark]'),
      buckets.map((b) => Math.max(0, Number(b && b.inputBytes) - Number(b && b.outputBytes))));
    renderKpiSpark(statsDashEl.querySelector('[data-kpi="avg"] [data-kpi-spark]'),
      buckets.map((b) => Number(b && b.avgDurationMs)));

    const canvas = statsDashEl.querySelector('[data-activity]');
    renderActivityCanvas(canvas, buckets);
    const axisFrom = statsDashEl.querySelector('[data-axis-from]');
    const axisTo = statsDashEl.querySelector('[data-axis-to]');
    if (buckets.length > 0) {
      const fmtDate = (ms) => new Date(ms).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      if (axisFrom) axisFrom.textContent = fmtDate(Number(buckets[0].tsMs));
      if (axisTo)   axisTo.textContent   = fmtDate(Number(buckets[buckets.length - 1].tsMs));
    }

    const pairs = formatsCache.get(statsActiveRange);
    renderFormatsDualBars(statsDashEl.querySelector('[data-formats]'),
      pairs ? pairs.pairs : []);

    let peakV = 0, peakTs = 0;
    for (const b of buckets) {
      const v = Math.max(0, Number(b.files)) + Math.max(0, Number(b.errs));
      if (v > peakV) { peakV = v; peakTs = Number(b.tsMs); }
    }
    const peakEl = statsDashEl.querySelector('[data-foot-peak]');
    const totalEl = statsDashEl.querySelector('[data-foot-total]');
    const agoEl = statsDashEl.querySelector('[data-foot-ago]');
    if (peakEl) {
      peakEl.textContent = peakV > 0
        ? fmt.int.format(peakV) + ' · ' + new Date(peakTs).toLocaleDateString(locale, { day: 'numeric', month: 'short' })
        : '—';
    }
    if (totalEl) totalEl.textContent = fmt.int.format(safeFiles);
    if (agoEl) {
      const sinceMs = Number(data && data.sinceMs);
      const untilMs = Number(data && data.untilMs);
      const ref = Number.isFinite(untilMs) ? untilMs : Date.now();
      agoEl.textContent = ago(ref - sinceMs, locale);
    }
  };

  // Compact "X minutes/hours/days ago" formatter; reused for
  // the dashboard footer updated stamp.
  const ago = (ms, loc) => {
    if (!Number.isFinite(ms) || ms < 0) return '—';
    const s = Math.floor(ms / 1000);
    if (s < 60) return s + 's';
    const m = Math.floor(s / 60);
    if (m < 60) return m + (loc === 'ru' ? ' мин' : ' min');
    const h = Math.floor(m / 60);
    if (h < 24) return h + (loc === 'ru' ? ' ч' : ' h');
    const d = Math.floor(h / 24);
    return d + (loc === 'ru' ? ' д' : ' d');
  };

  const clearStatsDash = () => {
    if (!statsDashEl) return;
    statsDashEl.querySelectorAll('[data-kpi-value]').forEach((el) => { el.textContent = '—'; });
    statsDashEl.querySelectorAll('[data-kpi-unit]').forEach((el) => { el.textContent = '—'; });
    statsDashEl.querySelectorAll('[data-kpi-spark]').forEach((el) => { el.innerHTML = ''; });
    const canvas = statsDashEl.querySelector('[data-activity]');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    const fmtHost = statsDashEl.querySelector('[data-formats]');
    if (fmtHost) fmtHost.innerHTML = '';
    const fromEl = statsDashEl.querySelector('[data-axis-from]');
    if (fromEl) fromEl.textContent = '—';
    const toEl = statsDashEl.querySelector('[data-axis-to]');
    if (toEl) toEl.textContent = '—';
    const peakEl = statsDashEl.querySelector('[data-foot-peak]');
    if (peakEl) peakEl.textContent = '—';
    const totalEl = statsDashEl.querySelector('[data-foot-total]');
    if (totalEl) totalEl.textContent = '—';
    const agoEl = statsDashEl.querySelector('[data-foot-ago]');
    if (agoEl) agoEl.textContent = '—';
  };

  const activateDashTab = (range) => {
    if (!statsDashEl) return;
    const tabs = statsDashEl.querySelectorAll('.stats-dash__tab');
    tabs.forEach((btn) => {
      const isActive = btn.getAttribute('data-range') === range;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    statsActiveRange = range;
  };

  const showDashError = () => {
    if (!statsDashEl) return;
    clearStatsDash();
    if (statsNoticeEl) {
      const key = statsFailureKind === 'validation'
        ? 'stats_error_validation'
        : statsFailureKind === 'backend'
          ? 'stats_error_backend'
          : statsFailureKind === 'network'
            ? 'stats_error_network'
            : 'stats_error';
      statsNoticeEl.setAttribute('data-i18n', key);
      statsNoticeEl.textContent = t(key);
      statsNoticeEl.classList.remove('is-hidden');
    }
  };

  const loadDashRange = (range) => {
    if (!statsDashEl) return;
    if (statsFailed) { showDashError(); return; }
    const cached = statsDashCache.get(range);
    if (cached) {
      renderStatsDash(cached);
      return;
    }
    if (statsDashFetchInFlight === range) return;
    statsDashFetchInFlight = range;
    Promise.all([fetchStatsRange(range), fetchFormatsRange(range).catch(() => null)])
      .then(([statsRes, formatsRes]) => {
        if (formatsRes) formatsCache.set(range, formatsRes.data);
        statsDashCache.set(range, statsRes.data);
        if (statsActiveRange === range) renderStatsDash(statsRes.data);
      })
      .catch((e) => {
        statsDashCache.delete(range);
        if (statsActiveRange === range) {
          if (e && /^HTTP \d+:validation$/.test(e.message)) statsFailureKind = 'validation';
          else if (e && /^HTTP \d+:backend$/.test(e.message)) statsFailureKind = 'backend';
          else statsFailureKind = 'network';
          showDashError();
        }
      })
      .finally(() => {
        if (statsDashFetchInFlight === range) statsDashFetchInFlight = null;
      });
  };

  // Wire tab clicks. CR-057 will replace this with a click-on-
  // sparkline handler that toggles #stats-dash; for now the
  // tabs are inactive (the section is hidden).
  if (statsDashEl) {
    statsDashEl.querySelectorAll('.stats-dash__tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const range = btn.getAttribute('data-range');
        if (!range || !RANGE_CONFIG[range]) return;
        activateDashTab(range);
        loadDashRange(range);
      });
    });
    window.addEventListener('resize', () => {
      if (!statsDashEl.hidden) {
        const data = statsDashCache.get(statsActiveRange);
        if (data) {
          const canvas = statsDashEl.querySelector('[data-activity]');
          renderActivityCanvas(canvas, data.buckets);
        }
      }
    });
  }

  // CR-057: honour the persisted view on init (the toggle +
  // close handlers are bound above; the DOM is reconciled here
  // after `statsDashEl` + `statsDashCache` are known).
  applyStatsView(statsView);

  // ---- init ----
  renderFormats();
  renderFormatsChips();
  loadFormatsMatrix();
  renderI18n();
  wireLangPill();
  // pill initial state is encoded in the HTML (aria-current on the current
  // locale's anchor link); no JS-driven aria mutation is needed.

  // CR-053: defensive 30-day window instead of since=0.
  // The server's parseStatsQuery:306-308 rewrites since=0
  // to (until - 24h); passing the window explicitly avoids
  // the sentinel path and makes the access log entry
  // self-documenting. The 30-day ceiling is below the
  // 31-day hard cap (stats-api.md § 2.1 validation rule).
  const statsWindowMs = 30 * 86400000;
  const statsUntilMs = Date.now();
  const statsSinceMs = statsUntilMs - statsWindowMs;
  const statsUrl = '/api/stats?since=' + statsSinceMs + '&until=' + statsUntilMs + '&bucket=day&status=ok';
  fetch(statsUrl, { credentials: 'omit' })
    .then((res) => {
      if (res.ok) return res.json();
      // CR-053: distinguish 400 (validation) from 5xx
      // (backend) so the user can see whether the
      // problem is the request shape (the page bug)
      // or the backend (outage / not routed).
      const kind = res.status === 400
        ? 'validation'
        : (res.status >= 500 ? 'backend' : 'backend');
      throw new Error('HTTP ' + res.status + ':' + kind);
    })
    .then((data) => {
      const totals = data?.totals;
      const fieldsAreValid = totals &&
        typeof totals.files === 'number' &&
        typeof totals.inputBytes === 'number' &&
        typeof totals.outputBytes === 'number' &&
        (totals.avgCompression == null || typeof totals.avgCompression === 'number');
      if (fieldsAreValid) {
        statsData = data;
        renderStatsFromCache();
        // CR-056: low-priority pre-fetch of the 30d range for
        // the expanded dashboard so the click-to-expand
        // (CR-057) is instant. status=all so the dashboard's
        // byStatus breakdown + okCount/errCount are populated.
        if (statsDashEl && !statsDashCache.has('30d')) {
          fetchStatsRange('30d')
            .then(({ data: dashData }) => { statsDashCache.set('30d', dashData); })
            .catch(() => { /* leave the cache empty; the tab click will retry */ });
          if (!formatsCache.has('30d')) {
            fetchFormatsRange('30d')
              .then(({ data: formatsData }) => { formatsCache.set('30d', formatsData); })
              .catch(() => { /* leave the cache empty; the tab click will retry */ });
          }
        }
      } else {
        statsFailed = true;
        statsFailureKind = 'validation';
        renderStatsFromCache();
      }
    })
    .catch((e) => {
      statsFailed = true;
      // fetch rejection (no res) is a transport failure
      // (CORS, reverse-proxy not routing /api/stats, server
      // unreachable). Status-bearing rejections come from
      // the inner .then throw above; route them to the
      // matching kind.
      if (e && /^HTTP \d+:validation$/.test(e.message)) {
        statsFailureKind = 'validation';
      } else if (e && /^HTTP \d+:backend$/.test(e.message)) {
        statsFailureKind = 'backend';
      } else {
        statsFailureKind = 'network';
      }
      renderStatsFromCache();
    });
})();
